import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { IconButton, CircularProgress, Chip } from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import SaveIcon from "@material-ui/icons/Save";
import CheckIcon from "@material-ui/icons/Check";
import LightbulbOutlinedIcon from "@material-ui/icons/EmojiObjects";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../services/api";
import useQueues from "../../hooks/useQueues";
import KoraxOnboardingInterview from "../../components/KoraxOnboardingInterview";

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1, width: "100%", height: "100%",
    display: "flex", flexDirection: "column",
    background: theme.palette.type === "dark" ? "#060D1A" : "#F0F4FF",
    fontFamily: "'Inter', system-ui, sans-serif",
  },

  // ── HEADER ──
  header: {
    flexShrink: 0, padding: "14px 24px",
    background: theme.palette.type === "dark" ? "#0D1626" : "#fff",
    borderBottom: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#E2E8F0"}`,
    display: "flex", alignItems: "center", gap: 12,
    boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
  },
  headerIcon: {
    width: 36, height: 36, borderRadius: 10,
    background: "linear-gradient(135deg,#0066FF,#00A3FF)",
    display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.1rem",
  },
  headerTitle: { fontWeight: 800, fontSize: "1rem", color: theme.palette.type === "dark" ? "#fff" : "#0A1628" },
  headerSub: { fontSize: "0.72rem", color: "#94A3B8", marginTop: 1 },

  // ── WIZARD STEPS BAR ──
  wizardBar: {
    flexShrink: 0,
    background: theme.palette.type === "dark" ? "#0A1628" : "#fff",
    borderBottom: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#E2E8F0"}`,
    padding: "0 24px",
    display: "flex", alignItems: "center", gap: 0,
    overflowX: "auto",
  },
  wizardStep: {
    display: "flex", alignItems: "center", gap: 8,
    padding: "14px 16px", cursor: "pointer",
    borderBottom: "2px solid transparent",
    transition: "all 0.2s", whiteSpace: "nowrap",
    color: "#94A3B8", fontWeight: 600, fontSize: "0.82rem",
  },
  wizardStepActive: {
    borderBottomColor: "#0066FF",
    color: "#0066FF",
  },
  wizardStepDone: {
    color: "#22C55E",
  },
  wizardStepNum: {
    width: 22, height: 22, borderRadius: "50%",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: "0.7rem", fontWeight: 800,
    background: "rgba(148,163,184,0.15)", color: "#94A3B8",
    transition: "all 0.2s",
  },
  wizardStepNumActive: {
    background: "#0066FF", color: "#fff",
  },
  wizardStepNumDone: {
    background: "#22C55E", color: "#fff",
  },
  wizardDivider: {
    width: 24, height: 1,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "#E2E8F0",
    flexShrink: 0,
  },

  // ── CONTENT ──
  content: {
    flex: 1, overflowY: "auto",
    padding: "28px 24px",
    ...theme.scrollbarStyles,
  },
  contentInner: {
    maxWidth: 700, margin: "0 auto",
    display: "flex", flexDirection: "column", gap: 20,
  },

  // ── SECTION CARD ──
  card: {
    background: theme.palette.type === "dark" ? "#0D1626" : "#fff",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.07)" : "#E2E8F0"}`,
    borderRadius: 16, padding: "20px 24px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
  },
  cardTitle: {
    fontWeight: 700, fontSize: "0.88rem",
    color: "#0066FF", textTransform: "uppercase",
    letterSpacing: "0.5px", marginBottom: 16,
    display: "flex", alignItems: "center", gap: 8,
  },
  fieldGroup: { display: "flex", flexDirection: "column", gap: 14 },
  fieldRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14,
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
  },
  fieldLabel: {
    fontWeight: 600, fontSize: "0.76rem",
    color: theme.palette.type === "dark" ? "#94A3B8" : "#475569",
    marginBottom: 5, display: "block",
  },
  fieldInput: {
    width: "100%", padding: "9px 12px", borderRadius: 8,
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "#E2E8F0"}`,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#F8FAFC",
    color: theme.palette.type === "dark" ? "#F1F5F9" : "#0A1628",
    fontSize: "0.84rem", outline: "none", boxSizing: "border-box",
    fontFamily: "inherit", transition: "border 0.15s",
    "&:focus": { borderColor: "#0066FF" },
  },
  fieldSelect: {
    width: "100%", padding: "9px 12px", borderRadius: 8,
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "#E2E8F0"}`,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#F8FAFC",
    color: theme.palette.type === "dark" ? "#F1F5F9" : "#0A1628",
    fontSize: "0.84rem", outline: "none", boxSizing: "border-box",
    cursor: "pointer", appearance: "none",
    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24'%3E%3Cpath fill='%2364748B' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E\")",
    backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center",
    paddingRight: 30,
  },
  fieldTextarea: {
    width: "100%", padding: "9px 12px", borderRadius: 8,
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "#E2E8F0"}`,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#F8FAFC",
    color: theme.palette.type === "dark" ? "#F1F5F9" : "#0A1628",
    fontSize: "0.82rem", outline: "none", boxSizing: "border-box",
    fontFamily: "inherit", resize: "vertical", lineHeight: 1.6,
    minHeight: 90, transition: "border 0.15s",
    "&:focus": { borderColor: "#0066FF" },
  },

  // ── CHIPS ──
  chipGroup: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4 },
  chip: {
    padding: "6px 14px", borderRadius: 20, fontSize: "0.76rem",
    fontWeight: 600, cursor: "pointer", border: "1px solid",
    transition: "all 0.12s",
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#F1F5F9",
    borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "#E2E8F0",
    color: theme.palette.type === "dark" ? "#94A3B8" : "#64748B",
  },
  chipActive: {
    background: "rgba(0,102,255,0.08) !important",
    borderColor: "#0066FF !important",
    color: "#0066FF !important",
  },

  // ── STEP CARDS (jornada) ──
  stepCard: {
    background: theme.palette.type === "dark" ? "#0D1626" : "#fff",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.07)" : "#E2E8F0"}`,
    borderRadius: 16, overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
    transition: "box-shadow 0.2s",
  },
  stepCardHeader: {
    padding: "12px 16px",
    display: "flex", alignItems: "center", gap: 10,
    borderBottom: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#F1F5F9"}`,
  },
  stepCardBody: {
    padding: "16px", display: "flex", flexDirection: "column", gap: 14,
  },
  stepNum: {
    width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontWeight: 800, fontSize: "0.8rem", color: "#fff",
  },
  stepTitle: {
    flex: 1, fontWeight: 700, fontSize: "0.88rem",
    color: theme.palette.type === "dark" ? "#fff" : "#0A1628",
    border: "none", outline: "none", background: "transparent",
    fontFamily: "inherit",
  },
  connector: {
    width: 2, height: 24, background: "rgba(0,102,255,0.15)",
    margin: "0 auto", position: "relative",
  },
  connectorDot: {
    width: 8, height: 8, borderRadius: "50%",
    background: "#0066FF", position: "absolute",
    left: "50%", transform: "translateX(-50%)", bottom: 0,
  },
  addStepBtn: {
    width: "100%", border: "2px dashed rgba(0,102,255,0.25)",
    borderRadius: 14, padding: "14px",
    background: "rgba(0,102,255,0.02)", color: "#0066FF",
    cursor: "pointer", display: "flex", alignItems: "center",
    justifyContent: "center", gap: 8,
    fontSize: "0.82rem", fontWeight: 700, transition: "all 0.2s",
  },

  // ── VALIDAÇÃO ──
  validBadge: {
    display: "inline-flex", alignItems: "center", gap: 4,
    padding: "2px 8px", borderRadius: 20, fontSize: "0.68rem", fontWeight: 700,
  },

  // ── SUGESTÃO ──
  suggestBtn: {
    display: "flex", alignItems: "center", gap: 5,
    padding: "5px 12px", borderRadius: 8,
    background: "rgba(0,102,255,0.06)", color: "#0066FF",
    border: "1px solid rgba(0,102,255,0.15)",
    fontSize: "0.74rem", fontWeight: 700, cursor: "pointer",
    transition: "all 0.15s", marginTop: 4, width: "fit-content",
  },
  suggestBox: {
    background: "rgba(0,102,255,0.04)",
    border: "1px solid rgba(0,102,255,0.15)",
    borderRadius: 10, padding: "10px 14px", marginTop: 8,
    display: "flex", flexDirection: "column", gap: 6,
  },
  suggestItem: {
    padding: "8px 12px", borderRadius: 8, fontSize: "0.78rem",
    cursor: "pointer", lineHeight: 1.5,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.03)" : "#fff",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#E2E8F0"}`,
    color: theme.palette.type === "dark" ? "#CBD5E1" : "#334155",
    transition: "all 0.12s",
    "&:hover": { borderColor: "#0066FF", background: "rgba(0,102,255,0.04)" },
  },

  // ── OBJEÇÕES ──
  objectionCard: {
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.07)" : "#E2E8F0"}`,
    borderRadius: 12, padding: "12px 14px",
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.02)" : "#F8FAFC",
  },

  // ── PREVIEW ──
  previewBubble: {
    maxWidth: "80%", padding: "10px 14px",
    borderRadius: 14, fontSize: "0.82rem", lineHeight: 1.6,
    marginBottom: 8,
  },
  previewIA: {
    alignSelf: "flex-start",
    background: theme.palette.type === "dark" ? "#1a2744" : "#EFF4FF",
    color: theme.palette.type === "dark" ? "#CBD5E1" : "#1e3a8a",
    borderBottomLeftRadius: 4,
  },
  previewLead: {
    alignSelf: "flex-end",
    background: "#0066FF", color: "#fff",
    borderBottomRightRadius: 4,
  },
  previewLabel: {
    fontSize: "0.68rem", fontWeight: 700,
    color: "#94A3B8", marginBottom: 4, marginTop: 12,
    textTransform: "uppercase", letterSpacing: "0.5px",
  },

  // ── FOOTER NAV ──
  footer: {
    flexShrink: 0, padding: "14px 24px",
    background: theme.palette.type === "dark" ? "#0D1626" : "#fff",
    borderTop: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#E2E8F0"}`,
    display: "flex", alignItems: "center", justifyContent: "space-between",
    boxShadow: "0 -1px 4px rgba(0,0,0,0.04)",
  },
  btnBack: {
    display: "flex", alignItems: "center", gap: 6,
    padding: "9px 18px", borderRadius: 10,
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "#E2E8F0"}`,
    background: "transparent", cursor: "pointer",
    fontWeight: 600, fontSize: "0.82rem",
    color: theme.palette.type === "dark" ? "#94A3B8" : "#64748B",
  },
  btnNext: {
    display: "flex", alignItems: "center", gap: 6,
    padding: "9px 22px", borderRadius: 10,
    background: "linear-gradient(135deg,#0066FF,#00A3FF)",
    color: "#fff", border: "none", cursor: "pointer",
    fontWeight: 700, fontSize: "0.84rem",
    boxShadow: "0 4px 14px rgba(0,102,255,0.25)",
    transition: "all 0.15s",
    "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  btnSave: {
    display: "flex", alignItems: "center", gap: 6,
    padding: "9px 22px", borderRadius: 10,
    background: "linear-gradient(135deg,#22C55E,#16A34A)",
    color: "#fff", border: "none", cursor: "pointer",
    fontWeight: 700, fontSize: "0.84rem",
    boxShadow: "0 4px 14px rgba(34,197,94,0.25)",
  },

  // ── RANGE SLIDER ──
  rangeRow: { display: "flex", flexDirection: "column", gap: 4 },
  rangeLabel: {
    display: "flex", justifyContent: "space-between",
    fontSize: "0.72rem", color: "#94A3B8",
  },

  // ── MEDIA ──
  mediaModal: {
    position: "fixed", inset: 0, zIndex: 1400,
    background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)",
    display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
  },
  mediaModalBox: {
    background: theme.palette.type === "dark" ? "#0D1626" : "#fff",
    borderRadius: 20, width: "100%", maxWidth: 480,
    overflow: "hidden", boxShadow: "0 24px 60px rgba(0,0,0,0.2)",
  },
  mediaModalHeader: {
    padding: "14px 18px",
    background: "linear-gradient(135deg,#0066FF,#00A3FF)",
    display: "flex", alignItems: "center", gap: 10,
  },
  mediaItem: {
    display: "flex", alignItems: "center", gap: 12,
    padding: "10px 16px", cursor: "pointer", transition: "all 0.15s",
    "&:hover": { background: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#F8FAFF" },
    borderBottom: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "#F1F5F9"}`,
  },

  premiumHero: {
    margin: "18px 24px 0",
    borderRadius: 22,
    padding: "18px 22px",
    background: "linear-gradient(135deg,#071B4D 0%,#005BFF 52%,#00A3FF 100%)",
    color: "#fff",
    display: "grid",
    gridTemplateColumns: "1fr 280px",
    gap: 18,
    alignItems: "center",
    boxShadow: "0 18px 48px rgba(0,102,255,0.22)",
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
  },
  premiumHeroTitle: {
    fontSize: 24,
    fontWeight: 950,
    letterSpacing: -0.7,
    margin: 0,
  },
  premiumHeroSub: {
    fontSize: 13,
    opacity: 0.86,
    marginTop: 6,
    maxWidth: 760,
    lineHeight: 1.5,
  },
  scoreBox: {
    borderRadius: 18,
    background: "rgba(255,255,255,0.14)",
    border: "1px solid rgba(255,255,255,0.22)",
    padding: 14,
    backdropFilter: "blur(8px)",
  },
  scoreNumber: {
    fontSize: 30,
    fontWeight: 950,
    lineHeight: 1,
  },
  progressTrack: {
    height: 9,
    borderRadius: 999,
    background: "rgba(255,255,255,0.24)",
    overflow: "hidden",
    marginTop: 10,
  },
  progressFill: {
    height: "100%",
    borderRadius: 999,
    background: "linear-gradient(90deg,#22C55E,#A7F3D0)",
  },
  premiumLayout: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 300px",
    gap: 18,
    maxWidth: 1180,
    margin: "0 auto",
    [theme.breakpoints.down("md")]: { gridTemplateColumns: "1fr" },
  },
  premiumMain: {
    minWidth: 0,
  },
  premiumAside: {
    position: "sticky",
    top: 12,
    alignSelf: "start",
    [theme.breakpoints.down("md")]: { position: "relative", top: 0 },
  },
  checklistCard: {
    background: theme.palette.type === "dark" ? "#0D1626" : "#fff",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "#E2E8F0"}`,
    borderRadius: 18,
    padding: 16,
    boxShadow: "0 12px 32px rgba(15,23,42,0.06)",
  },
  checklistTitle: {
    fontWeight: 900,
    fontSize: 14,
    color: theme.palette.type === "dark" ? "#F8FAFC" : "#0A1628",
    marginBottom: 12,
  },
  checklistItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    padding: "10px 11px",
    borderRadius: 12,
    marginBottom: 8,
    border: "1px solid",
  },
  quickStatGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 8,
    marginTop: 12,
  },
  quickStat: {
    borderRadius: 12,
    padding: 10,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#F8FAFC",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#E2E8F0"}`,
  },

  builderV2Shell: {
    width: "100%",
    maxWidth: 1440,
    margin: "0 auto",
    display: "grid",
    gridTemplateColumns: "310px minmax(0, 1fr)",
    gap: 16,
    alignItems: "start",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "280px minmax(0, 1fr)",
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      gap: 12,
    },
  },
  builderV2Panel: {
    background: "#FFFFFF",
    border: "1px solid #E2E8F0",
    borderRadius: 22,
    boxShadow: "0 18px 45px rgba(15,23,42,.06)",
    overflow: "hidden",
  },
  builderV2PanelPad: {
    padding: 16,
  },
  builderV2Editor: {
    background: "#FFFFFF",
    border: "1px solid #E2E8F0",
    borderRadius: 24,
    boxShadow: "0 22px 55px rgba(15,23,42,.07)",
    overflow: "hidden",
    minWidth: 0,
  },
  builderV2Preview: {
    background: "#FFFFFF",
    border: "1px solid #E2E8F0",
    borderRadius: 24,
    boxShadow: "0 18px 45px rgba(15,23,42,.06)",
    overflow: "hidden",
    position: "sticky",
    top: 12,
    [theme.breakpoints.down("md")]: {
      gridColumn: "1 / -1",
      position: "relative",
      top: 0,
    },
  },
  journeyMiniCard: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "11px 12px",
    borderRadius: 16,
    border: "1px solid #E2E8F0",
    background: "#FFFFFF",
    cursor: "pointer",
    marginBottom: 9,
    transition: "all .18s ease",
    "&:hover": {
      borderColor: "rgba(0,102,255,.35)",
      boxShadow: "0 10px 25px rgba(15,23,42,.06)",
      transform: "translateY(-1px)",
    },
  },
  journeyMiniCardActive: {
    borderColor: "rgba(0,102,255,.55) !important",
    background: "linear-gradient(135deg, rgba(0,102,255,.08), rgba(0,163,255,.04)) !important",
    boxShadow: "0 12px 28px rgba(0,102,255,.12) !important",
  },
  mobileHidden: {
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

}));

const WIZARD_STEPS = [
  { id: "agente", label: "Agente", icon: "⚙️" },
  { id: "contexto", label: "Contexto", icon: "🎯" },
  { id: "jornada", label: "Jornada", icon: "🗺️" },
  { id: "objecoes", label: "Objeções", icon: "🛡️" },
  { id: "preview", label: "Preview", icon: "👁️" },
];

const STEP_TYPES = [
  { id: "abertura", label: "Abertura", icon: "👋", desc: "Primeira mensagem ao lead" },
  { id: "qualificacao", label: "Qualificação", icon: "🎯", desc: "Entender o perfil do lead" },
  { id: "apresentacao", label: "Apresentação", icon: "🚀", desc: "Apresentar produto/serviço" },
  { id: "objecao", label: "Contorno de objeção", icon: "🛡️", desc: "Responder dúvidas e resistências" },
  { id: "proposta", label: "Proposta", icon: "💰", desc: "Apresentar oferta e preço" },
  { id: "fechamento", label: "Fechamento", icon: "🤝", desc: "Conduzir para a decisão" },
  { id: "followup", label: "Follow-up", icon: "📅", desc: "Reengajar lead frio" },
  { id: "custom", label: "Personalizada", icon: "✏️", desc: "Defina seu próprio objetivo" },
];

const STEP_COLORS = ["#0066FF","#7C3AED","#00A86B","#F59E0B","#EF4444","#06B6D4","#EC4899","#84CC16"];

const TYPE_CONFIG = {
  image: { emoji: "🖼️", label: "Imagem", color: "#0066FF", bg: "rgba(0,102,255,0.1)" },
  video: { emoji: "🎥", label: "Vídeo", color: "#7C3AED", bg: "rgba(124,58,237,0.1)" },
  audio: { emoji: "🎵", label: "Áudio", color: "#00A86B", bg: "rgba(0,168,107,0.1)" },
  pdf: { emoji: "📄", label: "PDF", color: "#F59E0B", bg: "rgba(245,158,11,0.1)" },
};

const getTrainingChecklist = (config, steps, objections) => {
  const completeSteps = steps.filter((step, index) =>
    (step.objective || "").trim() &&
    (step.finalQuestion || "").trim() &&
    (index !== 0 || (step.message || "").trim())
  ).length;
  return [
    { label: "Identidade da IA", ok: !!(config.name || "").trim(), value: config.name || "Pendente" },
    { label: "Produto/serviço", ok: !!(config.product || "").trim(), value: config.product || "Pendente" },
    { label: "Público-alvo", ok: !!(config.audience || "").trim(), value: config.audience || "Pendente" },
    { label: "Tom de voz", ok: !!config.tone, value: config.tone || "Pendente" },
    { label: "Jornada comercial", ok: steps.length >= 2 && completeSteps === steps.length, value: `${completeSteps}/${steps.length}` },
    { label: "Objeções", ok: objections.length > 0, value: `${objections.length}` },
  ];
};

const getTrainingScore = (config, steps, objections) => {
  const checklist = getTrainingChecklist(config, steps, objections);
  const base = checklist.reduce((acc, item) => acc + (item.ok ? 1 : 0), 0);
  return Math.round((base / checklist.length) * 100);
};

const TONS = [
  { label: "Vendedor humanizado", value: "vendedor humanizado" },
  { label: "Consultor especialista", value: "consultor especialista" },
  { label: "Amigável e descontraído", value: "amigável e descontraído" },
  { label: "Formal e profissional", value: "formal e profissional" },
  { label: "Direto e objetivo", value: "direto e objetivo" },
];

const SUGESTOES = {
  abertura: {
    mensagens: [
      "Oi! Tudo bem? 😊 Seja bem-vindo! Vou te ajudar por aqui.",
      "Olá! 😊 Que bom ter você por aqui. Vamos começar?",
      "Oi! Tudo certo? 😊 Seja bem-vindo ao nosso atendimento."
    ],
    perguntas: [
      "Antes de continuar, como posso te chamar?",
      "Como você prefere que eu te chame?",
      "Qual é o seu nome?"
    ]
  },
  qualificacao: {
    mensagens: [
      "Considere o que o lead acabou de informar e faça uma ponte curta antes de buscar a próxima informação.",
      "Entenda o cenário do lead sem transformar a etapa em interrogatório. Use respostas anteriores para contextualizar a próxima pergunta.",
      "Busque somente a informação que ainda estiver faltando e conecte a pergunta ao contexto real da conversa."
    ],
    perguntas: [
      "Hoje vocês atendem com quantas pessoas no WhatsApp?",
      "Qual é o principal desafio hoje no atendimento?",
      "Vocês já usam alguma ferramenta para organizar esse processo?"
    ]
  },
  apresentacao: {
    mensagens: [
      "Apresente apenas os benefícios relacionados ao problema ou oportunidade que o lead revelou.",
      "Conecte a solução ao contexto já descoberto, evitando listas grandes de funcionalidades.",
      "Mostre como a solução se aplica ao cenário do lead e mantenha a explicação curta e prática."
    ],
    perguntas: [
      "Isso faz sentido para o cenário de vocês?",
      "Essa solução atende o que vocês precisam hoje?",
      "Esse caminho ajudaria na operação de vocês?"
    ]
  },
  objecao: {
    mensagens: [
      "Responda a objeção específica do lead antes de retomar a condução comercial.",
      "Reconheça a preocupação sem usar frases automáticas de concordância e esclareça apenas o ponto levantado.",
      "Use o contexto da objeção para responder de forma objetiva e depois identifique se ainda existe alguma barreira relevante."
    ],
    perguntas: [
      "Esse ponto ficou mais claro?",
      "Existe alguma outra preocupação importante antes de avançar?",
      "O que você precisaria entender melhor sobre isso?"
    ]
  },
  proposta: {
    mensagens: [
      "Apresente a proposta conectada ao que foi descoberto na conversa, sem repetir todo o diagnóstico.",
      "Mostre a condição ou oferta relevante de forma objetiva e explique somente o necessário para a decisão.",
      "Relacione a proposta ao cenário do lead e evite pressão comercial artificial."
    ],
    perguntas: [
      "Essa condição faz sentido para vocês?",
      "Quer avançar com essa opção?",
      "Existe algum ponto da proposta que precisa ser esclarecido?"
    ]
  },
  fechamento: {
    mensagens: [
      "Quando houver intenção clara, conduza para o próximo passo sem reabrir perguntas de qualificação já respondidas.",
      "Facilite a decisão do lead e apresente o próximo passo disponível de forma objetiva.",
      "Se o cliente já decidiu avançar, priorize executar a ação correta em vez de prolongar a conversa."
    ],
    perguntas: [
      "Quer seguir com esse próximo passo?",
      "Prefere começar agora ou falar com alguém do time?",
      "Qual opção faz mais sentido para você?"
    ]
  },
  followup: {
    mensagens: [
      "Retome o contexto da conversa anterior sem parecer uma cobrança automática.",
      "Lembre brevemente o ponto em que a conversa parou e ofereça ajuda para continuar.",
      "Faça o follow-up com contexto e evite mensagens genéricas que poderiam servir para qualquer lead."
    ],
    perguntas: [
      "Quer continuar de onde paramos?",
      "Ficou alguma dúvida antes de avançarmos?",
      "Ainda faz sentido seguir com isso?"
    ]
  },
  custom: {
    mensagens: [
      "Descreva o comportamento esperado nesta etapa e o contexto que a IA deve considerar.",
      "Explique a intenção da etapa em vez de escrever uma fala fixa para a IA copiar.",
      "Oriente como a IA deve interpretar este momento da conversa e qual resultado deve buscar."
    ],
    perguntas: [
      "Qual informação precisa ficar clara antes de avançar?",
      "O que precisamos descobrir nesta etapa?",
      "Qual é a principal informação necessária agora?"
    ]
  }
};

const defaultStep = (index) => ({
  id: Date.now() + index,
  type: STEP_TYPES[Math.min(index, STEP_TYPES.length - 1)].id,
  title: STEP_TYPES[Math.min(index, STEP_TYPES.length - 1)].label,
  message: "",
  medias: [],
  objective: "",
  triggers: "",
  finalQuestion: "",
  showSuggest: false,
  automation: {
    enabled: false,
    followUps: [
      { id: 1, enabled: false, delay: 30, unit: "minutes", message: "" },
      { id: 2, enabled: false, delay: 4, unit: "hours", message: "" },
      { id: 3, enabled: false, delay: 1, unit: "days", message: "" }
    ]
  },
});

const KoraxIABuilder = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { promptId } = useParams();

  const params = new URLSearchParams(location.search);

  const onboardingMode =
    params.get("mode") === "onboarding";

  const directJourney =
    params.get("experience") === "journey";

  console.log("KORAX DEBUG", {
    promptId,
    directJourney,
    onboardingMode,
    pathname: location.pathname,
    search: location.search
  });
  const { findAll: findQueues } = useQueues();

  const [currentStep, setCurrentStep] = useState(directJourney ? 2 : 0);
  const [saving, setSaving] = useState(false);
  const [queues, setQueues] = useState([]);
  const [medias, setMedias] = useState([]);
  const [mediaModalOpen, setMediaModalOpen] = useState(false);
  const [mediaTargetStep, setMediaTargetStep] = useState(null);
  const [typePickerOpen, setTypePickerOpen] = useState(false);
  const [selectedJourneyStepId, setSelectedJourneyStepId] = useState(null);
  const [stepEditorTab, setStepEditorTab] = useState("conversation");
  const [labMessages, setLabMessages] = useState([]);
  const [labInput, setLabInput] = useState("");
  const [labLoading, setLabLoading] = useState(false);

  const [config, setConfig] = useState({
    name: "",
    apiKey: "",
    model: "gpt-4o-mini",
    queueId: "",
    maxTokens: 800,
    temperature: 0.7,
    maxMessages: 20,
    tone: "vendedor humanizado",
    publicIdentityMode: "agent",
    publicCompanyName: "",
    openingMessage: "",
    replyLength: "standard",
    qualificationFields: [],
    finalAction: "none",
    finalSummary: true,
    finalConfirmation: true,
    finalInstructions: "",
    agentRole: "Comercial",
    mainGoal: "Vender pelo WhatsApp",
    product: "",
    audience: "",
    companyInfo: "",
    productDetails: "",
    humanTransferMessage: "Vou te conectar com um especialista agora! Um momento 😊",
    useJourney: directJourney,
  });

  const [objections, setObjections] = useState([]);
  const [objectionForm, setObjectionForm] = useState({ objection: "", answer: "" });

  const [steps, setSteps] = useState([
    {
      ...defaultStep(0),
      type: "abertura",
      title: "Abertura",
      objective: "Receber o lead, apresentar o atendimento de forma natural e identificar como ele prefere ser chamado.",
      message: "Oi! Tudo bem? 😊 Seja bem-vindo! Vou te ajudar por aqui.",
      finalQuestion: "Antes de continuar, como posso te chamar?"
    },
    {
      ...defaultStep(1),
      type: "qualificacao",
      title: "Qualificação",
      objective: "Entender uma informação relevante sobre o cenário atual do lead.",
      message: "Use o que o lead já informou para contextualizar a próxima pergunta e evite transformar a qualificação em formulário.",
      finalQuestion: "Qual é o principal desafio hoje no atendimento?"
    },
    {
      ...defaultStep(2),
      type: "apresentacao",
      title: "Apresentação",
      objective: "Conectar a solução ao problema ou oportunidade identificada durante a conversa.",
      message: "Apresente somente os benefícios relacionados ao contexto que o lead revelou.",
      finalQuestion: "Isso faz sentido para o cenário de vocês?"
    },
  ]);

  useEffect(() => {
    findQueues().then(setQueues).catch(() => {});
    api.get("/cavia-medias").then(r => setMedias(r.data || [])).catch(() => {});
    if (promptId) {
      api.get(`/prompt/${promptId}`).then(({ data }) => {
        if (data.builderConfig) {
          if (data.builderConfig.config) setConfig(prev => ({ ...prev, ...data.builderConfig.config }));
          if (data.builderConfig.steps) setSteps(data.builderConfig.steps);
          if (data.builderConfig.objections) setObjections(data.builderConfig.objections);
        }
      }).then(() => {
        if (directJourney) {
          setSelectedExperience("journey");
          setCurrentStep(2);
          setConfig(prev => ({ ...prev, useJourney: true }));
        }
      }).catch(() => {});
    } else if (directJourney) {
      setSelectedExperience("journey");
      setCurrentStep(2);
      setConfig(prev => ({ ...prev, useJourney: true }));
    }
  }, []);

  const updateConfig = (field, value) => setConfig(c => ({ ...c, [field]: value }));
  const updateStep = (id, field, value) => setSteps(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s));

  const updateStepFlow = (stepId, field, value) => {
    setSteps(prev => prev.map(step => {
      if (step.id !== stepId) return step;
      const current = step.flow || { action: "next", message: "", link: "" };
      return { ...step, flow: { ...current, [field]: value } };
    }));
  };

  const getDefaultAutomation = () => ({
    enabled: false,
    followUps: [
      { id: 1, enabled: false, delay: 30, unit: "minutes", message: "" },
      { id: 2, enabled: false, delay: 4, unit: "hours", message: "" },
      { id: 3, enabled: false, delay: 1, unit: "days", message: "" }
    ]
  });

  const updateStepAutomation = (stepId, updater) => {
    setSteps(prev => prev.map(step => {
      if (step.id !== stepId) return step;
      const current = step.automation || getDefaultAutomation();
      return { ...step, automation: updater(current) };
    }));
  };

  const updateStepFollowUp = (stepId, followUpId, field, value) => {
    updateStepAutomation(stepId, automation => ({
      ...automation,
      followUps: (automation.followUps || getDefaultAutomation().followUps).map(fu =>
        fu.id === followUpId ? { ...fu, [field]: value } : fu
      )
    }));
  };
  const removeStep = (id) => setSteps(prev => prev.filter(s => s.id !== id));
  const addStep = (type) => {
    const t = STEP_TYPES.find(s => s.id === type) || STEP_TYPES[7];
    setSteps(prev => [...prev, { ...defaultStep(prev.length), type: t.id, title: t.label }]);
    setTypePickerOpen(false);
  };
  const addMediaToStep = (stepId, media) => {
    setSteps(prev => prev.map(s => s.id === stepId ? {
      ...s, medias: [...(s.medias || []), { id: media.id, name: media.name, type: media.type, mediaKey: media.mediaKey }]
    } : s));
    setMediaModalOpen(false);
  };
  const removeMediaFromStep = (stepId, mediaId) => {
    setSteps(prev => prev.map(s => s.id === stepId ? { ...s, medias: s.medias.filter(m => m.id !== mediaId) } : s));
  };
  const addObjection = () => {
    if (!objectionForm.objection.trim() || !objectionForm.answer.trim()) return toast.error("Preencha a objeção e a resposta");
    setObjections(prev => [...prev, { id: Date.now(), ...objectionForm }]);
    setObjectionForm({ objection: "", answer: "" });
  };
  const removeObjection = (id) => setObjections(prev => prev.filter(o => o.id !== id));

  const stepValid = (step, providedIndex) => {
    const resolvedIndex =
      typeof providedIndex === "number"
        ? providedIndex
        : steps.findIndex(s => s.id === step?.id);

    return (
      (step.objective || "").trim().length > 0 &&
      (step.finalQuestion || "").trim().length > 0 &&
      (resolvedIndex !== 0 || (step.message || "").trim().length > 0)
    );
  };

  const trainingScore = getTrainingScore(config, steps, objections);
  const trainingChecklist = getTrainingChecklist(config, steps, objections);

  // Router da experiência do Builder
  // ======================================================
// EXPERIENCE ROUTER
//
// false -> Jornada Comercial
// true  -> Atendimento Inteligente
//
// Futuras experiências:
//
// SDR
// Pós-venda
// Cobrança
// Suporte Técnico
//
// Todas compartilharão o mesmo Builder.
// ======================================================
const isSmartMode = directJourney ? false : !config.useJourney;

const [selectedExperience, setSelectedExperience] = useState(directJourney ? "journey" : null);


  
const smartChecklist = [
  {
    label: "Empresa",
    ok: !!config.companyInfo?.trim(),
    value: config.companyInfo || "Não informado"
  },
  {
    label: "Produtos",
    ok: !!config.productDetails?.trim(),
    value: config.productDetails || "Não informado"
  },
  {
    label: "Tom de voz",
    ok: !!config.tone,
    value: config.tone || "Padrão"
  },
  {
    label: "Transferência",
    ok: !!config.humanTransferMessage,
    value: config.humanTransferMessage || "Padrão"
  },
  {
    label: "IA pronta",
    ok: !!config.companyInfo && !!config.productDetails,
    value: (!!config.companyInfo && !!config.productDetails)
      ? "Treinamento completo"
      : "Treinamento incompleto"
  }
];

const completedChecklist = trainingChecklist.filter(item => item.ok).length;

  const canAdvance = () => {
    if (currentStep === 0) return config.name.trim().length >= 2;
    if (currentStep === 1) return config.product.trim().length >= 2 && config.audience.trim().length >= 2;
    return true;
  };

  const generatePrompt = () => {
    const trainingFields = Array.isArray(config.qualificationFields)
      ? config.qualificationFields.filter(f => (f.label || "").trim())
      : [];

    const identityMode = config.publicIdentityMode || "agent";
    const brand = (config.publicCompanyName || "").trim();

    const identityRule = identityMode === "company"
      ? `Apresente-se somente como assistente da ${brand || "empresa configurada"}; não use o nome interno do agente.`
      : identityMode === "custom"
        ? "Utilize somente a apresentação personalizada configurada."
        : identityMode === "none"
          ? "Não faça apresentação automática; responda diretamente ao cliente."
          : `Seu nome público é ${config.name || "Assistente"}.`;

    const replyRule = config.replyLength === "short"
      ? "Prefira uma ou duas frases curtas e apenas uma pergunta principal por resposta."
      : config.replyLength === "detailed"
        ? "Explique detalhadamente quando necessário, mantendo clareza e objetividade."
        : "Use respostas naturais, proporcionais à pergunta do cliente.";

    const collectionRule = trainingFields.length
      ? trainingFields.map(f =>
          `- ${f.label} | Tipo: ${f.type || "text"} | ${f.required ? "Obrigatório" : "Opcional"}`
        ).join("\n")
      : "Nenhum campo adicional configurado.";
    const finalActions = {
      human: "Encaminhar para atendimento humano",
      schedule: "Solicitar agendamento usando a agenda disponível",
      crm: "Registrar oportunidade usando o CRM disponível",
      answer: "Orientar o cliente sem ação operacional"
    };

    const finalRule = finalActions[config.finalAction]
      ? [
          "# FINALIZAÇÃO DO OBJETIVO",
          "Resultado desejado: " + finalActions[config.finalAction],
          config.finalSummary
            ? "Antes de concluir, apresente resumo dos dados relevantes."
            : "Não exija resumo quando ele não for necessário.",
          config.finalConfirmation
            ? "Solicite confirmação do cliente antes de finalizar."
            : "Não exija confirmação adicional por padrão.",
          config.finalInstructions || "",
          "Permita perguntas fora do fluxo e retome a etapa pendente.",
          "Nunca anuncie transferência, agendamento ou registro",
          "como concluído sem confirmação real do sistema.",
          "Respeite as regras operacionais e campos obrigatórios."
        ].join("\\n")
      : "";

    const agentName = config.name || "Assistente";
    const product = config.product || "nosso produto/serviço";
    const audience = config.audience || "leads interessados";

    let prompt = `# IDENTIDADE
Você é responsável pelo atendimento no WhatsApp da empresa configurada. Respeite a identidade pública definida abaixo.
Produto/Serviço: ${product}
Público-alvo: ${audience}
Tom de voz: ${config.tone}
Função principal do agente: ${config.agentRole || "Comercial"}
Resultado principal desejado: ${config.mainGoal || "Vender pelo WhatsApp"}

# CONFIGURAÇÃO VISUAL DE ATENDIMENTO
${identityRule}
Saudação personalizada: ${config.openingMessage || "Use uma saudação contextual, sem repeti-la."}
${replyRule}
Não reinicie a apresentação durante a mesma conversa.

${finalRule}

# DADOS A COLETAR PARA O OBJETIVO
${collectionRule}
Aproveite informações já fornecidas em qualquer ordem.
Pergunte somente o que ainda faltar.
Responda dúvidas fora do fluxo e retome o objetivo naturalmente.
Nunca invente valores para campos ausentes.

Fale de forma natural, humana e coerente com a identidade configurada.
Não invente uma identidade diferente da cadastrada.
Se o treinamento da empresa disser que o atendimento é feito por IA, assistente virtual ou automação, você pode informar isso naturalmente quando for relevante.

REGRAS DE IDENTIDADE:
- ${identityRule}
- Você representa exclusivamente a empresa, produto ou serviço configurado
- Nunca confunda o nome do lead com sua própria identidade
- Nunca diga que trabalha na empresa do cliente
- Preserve sua identidade durante toda a conversa

REGRAS DE INTERPRETAÇÃO DO HISTÓRICO:
- Mensagens marcadas como "MENSAGEM DO CLIENTE" representam dúvidas, respostas e intenções do lead
- Mensagens marcadas como "MENSAGEM ENVIADA PELA EQUIPE/ASSISTENTE" representam o que já foi dito na conversa
- Considere o histórico antes de perguntar algo
- Não pergunte novamente uma informação que o cliente já forneceu claramente
- Se o cliente responder uma etapa e fizer uma pergunta na mesma mensagem, considere as duas partes da mensagem

# COMO CONVERSAR
- Prefira mensagens curtas e fáceis de ler no WhatsApp
- Organize a resposta em pequenos blocos quando houver mais de uma ideia
- Cada bloco deve carregar uma ideia principal
- Quando o cliente trouxer uma informação relevante, conecte a resposta a essa informação antes de seguir a conversa
- Evite iniciar toda resposta com confirmações genéricas como "Entendi", "Perfeito", "Ótimo", "Legal" ou equivalentes
- Não substitua uma reação contextual por uma frase automática de confirmação
- Responda primeiro dúvidas ou perguntas diretas do cliente
- Depois retome naturalmente o objetivo comercial quando ainda houver algo necessário a descobrir
- Faça no máximo UMA pergunta principal por resposta
- Pergunte somente quando houver uma informação realmente necessária para continuar
- Não repita a mesma pergunta com palavras diferentes
- Não transforme a conversa em interrogatório
- Nunca envie textos longos quando a mesma ideia puder ser comunicada de forma mais simples

# EMPRESA REPRESENTADA
${config.companyInfo || "Nenhuma informação adicional da empresa foi cadastrada."}

# CONHECIMENTO SOBRE PRODUTO/SERVIÇO
Nunca invente informações fora deste conteúdo.
Quando uma informação factual não estiver disponível, deixe claro que não possui essa confirmação no momento.
${config.productDetails || "Nenhuma informação adicional do produto/serviço foi cadastrada."}

${config.useJourney ? `# MOTOR DE CONDUÇÃO COMERCIAL
A jornada abaixo define os objetivos comerciais e as informações que precisam ser descobertas durante a conversa.

REGRAS DE CONDUÇÃO:
- Respeite a ordem lógica da jornada sem transformar as etapas em um formulário
- Cada etapa representa um objetivo, não uma frase fixa
- A pergunta cadastrada em uma etapa é uma referência semântica da informação que precisa ser descoberta
- Formule perguntas de maneira natural e contextual
- Se a informação necessária já estiver presente no histórico ou na mensagem atual, não pergunte novamente
- Se a resposta do lead for vaga e a informação ainda for necessária, peça esclarecimento de forma simples
- Se o lead antecipar uma dúvida sobre preço, demonstração, compra ou outro assunto comercial, responda primeiro ao que ele perguntou
- Depois da resposta, retome o objetivo atual somente se ainda houver informação necessária
- Não bloqueie uma intenção comercial clara apenas para reproduzir uma pergunta antiga
- O controle de avanço da jornada é feito pela aplicação; sua função é conversar naturalmente dentro do objetivo atual
` : `# OBJETIVO DO ATENDIMENTO
Seu objetivo é representar fielmente a empresa e ajudar o cliente de forma natural.

REGRAS DE ATENDIMENTO:
- Responda primeiro a dúvida do cliente
- Faça perguntas apenas quando forem necessárias
- Nunca force um fluxo comercial
- Nunca invente informações
- Quando não possuir uma informação factual, deixe isso claro
- Conduza a conversa naturalmente até ajudar o cliente
`}

${objections.length > 0 ? `# BANCO DE OBJEÇÕES
${objections.map((item, i) => `Objeção ${i+1}: ${item.objection}\nResposta recomendada: ${item.answer}`).join("\n\n")}
` : ""}
`;

    if (config.useJourney) {
      prompt += `# JORNADA DE ATENDIMENTO

As etapas abaixo descrevem objetivos e informações que devem orientar a conversa.
Não trate mensagens modelo ou perguntas cadastradas como textos obrigatórios.
Adapte a linguagem ao que o cliente acabou de dizer e ao histórico já coletado.

`;

      steps.forEach((step, i) => {
        const t = STEP_TYPES.find(s => s.id === step.type) || STEP_TYPES[7];

        prompt += `## ETAPA ${i+1} — ${step.title.toUpperCase()} ${t.icon}\n`;

        if (step.objective) {
          prompt += `Objetivo da etapa: ${step.objective}\n`;
        }

        if (step.triggers) {
          prompt += `Sinal de conclusão/avanço: ${step.triggers}\n`;
        }

        if (step.message) {
          if (i === 0) {
            prompt += `Mensagem inicial configurada:\n"${step.message}"\n`;
            prompt += `Esta é a mensagem de abertura configurada para iniciar a conversa.\n`;
          } else {
            prompt += `Contexto sugerido da etapa:\n"${step.message}"\n`;
            prompt += `Use este conteúdo apenas como contexto e intenção. Não copie automaticamente confirmações ou frases genéricas presentes nele.\n`;
          }
        }

        if (step.finalQuestion) {
          prompt += `Informação que precisa ser descoberta: "${step.finalQuestion}"\n`;
          prompt += `Use essa pergunta como referência semântica. Você pode reformulá-la naturalmente e não deve repeti-la se o cliente já tiver fornecido a informação.\n`;
        }

        if (step.medias && step.medias.length > 0) {
          step.medias.forEach(m => {
            prompt += `MÍDIA OBRIGATÓRIA: [ENVIAR_MIDIA: ${m.mediaKey}]\n`;
            prompt += `Após enviar a mídia, continue a conversa naturalmente de acordo com o objetivo atual.\n`;
          });
        }

        prompt += `\n`;
      });
    }

    prompt += `# REGRAS FINAIS
- Prefira mensagens curtas e conversacionais
- Não invente informações sobre produto, serviço ou empresa
- Preserve a identidade configurada
- Responda dúvidas antes de conduzir para a próxima informação necessária
- Faça perguntas somente quando forem necessárias para continuar
- Não faça mais de uma pergunta principal por resposta
- Não repita informação ou pergunta já respondida
- Se o lead pedir humano: "${config.humanTransferMessage || "Vou te conectar com um especialista agora! Um momento 😊"}"
- Não envie áudios, imagens ou vídeos além dos comandos de mídia configurados
- Seja consultivo e natural, nunca insistente
`;

    return prompt;
  };

  const handleSave = async () => {
    if (!config.name) {
      toast.error("Informe o nome do agente");
      return false;
    }

    setSaving(true);
    try {
      const prompt = generatePrompt();
      const payload = {
        name: config.name, prompt,
        apiKey: config.apiKey || "", model: config.model || "gpt-4o-mini",
        queueId: config.queueId || undefined, maxTokens: config.maxTokens,
        temperature: config.temperature, voice: "texto",
        voiceKey: "", voiceRegion: "", maxMessages: config.maxMessages,
        builderConfig: { config, steps, objections },
      };
      let savedPromptId = promptId;

      if (promptId) {
        const { data } = await api.put(`/prompt/${promptId}`, payload);
        savedPromptId = data.id;
        toast.success("Agente atualizado com sucesso! ✅");
      } else {
        const { data } = await api.post("/prompt", payload);
        savedPromptId = data.id;
        toast.success("Agente criado com sucesso! 🎉");
      }
      setSaving(false);

      console.log("[CAVIA] agente salvo", {
        savedPromptId,
        promptIdOriginal: promptId,
        onboardingMode
      });

      if (onboardingMode) {
        try {
          const storageKey = "@Korax:setupFlow";
          const storedState = JSON.parse(
            localStorage.getItem(storageKey) || "{}"
          );

          localStorage.setItem(
            storageKey,
            JSON.stringify({
              ...storedState,
              currentStep: 5,
              ai: {
                ...(storedState.ai || {}),
                trained: true,
                promptId: Number(savedPromptId)
              }
            })
          );
        } catch (error) {
          console.error("Erro ao persistir o agente do onboarding:", error);
        }

        history.push(`/onboarding?step=test&created=cavia&promptId=${savedPromptId}`);
      } else {
        history.push("/prompts");
      }

      return true;
    } catch (e) {
      toast.error("Erro ao salvar agente");
      setSaving(false);
      return false;
    }
  };

  // ── RENDER STEPS DO WIZARD ──
  const renderAgente = () => (
    <div className={classes.contentInner}>
      <div style={{
        background: "#fff",
        border: "1px solid #E2E8F0",
        borderRadius: 24,
        padding: 22,
        boxShadow: "0 18px 45px rgba(15,23,42,.06)"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 22 }}>
          <div style={{
            width: 56,
            height: 56,
            borderRadius: 18,
            background: "linear-gradient(135deg,#0066FF,#00A3FF)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 26,
            boxShadow: "0 14px 28px rgba(0,102,255,.22)",
            flexShrink: 0
          }}>
            🤖
          </div>
          <div>
            <div style={{ fontSize: 22, fontWeight: 950, color: "#0F172A", letterSpacing: -0.5 }}>
              Quem é o seu agente?
            </div>
            <div style={{ fontSize: 13, color: "#64748B", marginTop: 4 }}>
              Configure a identidade, o setor e o objetivo principal do atendimento.
            </div>
          </div>
        </div>

        <div className={classes.fieldRow}>
          <div>
            <label className={classes.fieldLabel}>Nome do agente *</label>
            <input className={classes.fieldInput} value={config.name}
              onChange={e => updateConfig("name", e.target.value)}
              placeholder="Ex: Sofia, Assistente Comercial, Consultor Korax" />
          </div>

          <div>
            <label className={classes.fieldLabel}>Atendimento</label>

            {queues.length <= 1 ? (
              <div
                className={classes.fieldInput}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 9,
                  cursor: "default",
                  background: "#F8FAFC",
                  color: "#334155",
                  fontWeight: 700
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: "#22C55E",
                    flexShrink: 0
                  }}
                />
                {queues[0]?.name || "Atendimento Geral"}
              </div>
            ) : (
              <select
                className={classes.fieldSelect}
                value={config.queueId || ""}
                onChange={e => updateConfig("queueId", e.target.value)}
              >
                <option value="">Atendimento Geral (Padrão)</option>
                {queues.map(q => (
                  <option key={q.id} value={q.id}>
                    {q.name}
                  </option>
                ))}
              </select>
            )}

            <div
              style={{
                marginTop: 6,
                fontSize: 12,
                color: "#64748B"
              }}
            >
              {queues.length <= 1
                ? "Este agente será conectado automaticamente ao atendimento da empresa."
                : "Escolha em qual atendimento este agente irá atuar."}
            </div>
          </div>
        </div>

        <div style={{ marginTop: 20 }}>
          <label className={classes.fieldLabel}>Qual será o papel principal?</label>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 10 }}>
            {["Vendedor", "Pré-vendedor", "Recepcionista", "Agendador", "Suporte", "Pós-venda"].map(role => (
              <button
                key={role}
                type="button"
                onClick={() => updateConfig("agentRole", role)}
                style={{
                  border: `1px solid ${config.agentRole === role ? "#0066FF" : "#E2E8F0"}`,
                  background: config.agentRole === role ? "rgba(0,102,255,.07)" : "#F8FAFC",
                  color: config.agentRole === role ? "#0066FF" : "#334155",
                  borderRadius: 14,
                  padding: "12px 10px",
                  fontWeight: 900,
                  cursor: "pointer",
                  fontSize: 12.5
                }}
              >
                {role}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginTop: 20 }}>
          <label className={classes.fieldLabel}>Qual resultado você quer alcançar?</label>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10 }}>
            {[
              "Captar novos leads",
              "Qualificar interessados",
              "Vender pelo WhatsApp",
              "Agendar atendimentos",
              "Responder dúvidas",
              "Reativar oportunidades"
            ].map(goal => (
              <button
                key={goal}
                type="button"
                onClick={() => updateConfig("mainGoal", goal)}
                style={{
                  border: `1px solid ${config.mainGoal === goal ? "#00A86B" : "#E2E8F0"}`,
                  background: config.mainGoal === goal ? "rgba(0,168,107,.08)" : "#FFFFFF",
                  color: config.mainGoal === goal ? "#00875A" : "#334155",
                  borderRadius: 14,
                  padding: "12px 10px",
                  fontWeight: 850,
                  cursor: "pointer",
                  fontSize: 12
                }}
              >
                {goal}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderContexto = () => (
    <div className={classes.contentInner}>
      <div className={classes.card}>
        <div className={classes.cardTitle}>🏢 Sobre o que sua empresa vende</div>
        <div className={classes.fieldGroup}>
          <div className={classes.fieldRow}>
            <div>
              <label className={classes.fieldLabel}>O que você vende? *</label>
              <input className={classes.fieldInput} value={config.product}
                onChange={e => updateConfig("product", e.target.value)}
                placeholder="Ex: Plataforma de atendimento, clínica estética, semijoias, imobiliária..." />
            </div>
            <div>
              <label className={classes.fieldLabel}>Para quem você vende? *</label>
              <input className={classes.fieldInput} value={config.audience}
                onChange={e => updateConfig("audience", e.target.value)}
                placeholder="Ex: empresários, pacientes, noivas, clientes locais, leads do Instagram..." />
            </div>
          </div>
          <div>
            <label className={classes.fieldLabel}>Tom de voz</label>
            <div className={classes.chipGroup}>
              {TONS.map(t => (
                <div key={t.value}
                  className={`${classes.chip} ${config.tone === t.value ? classes.chipActive : ""}`}
                  onClick={() => updateConfig("tone", t.value)}>
                  {t.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className={classes.card}>
        <div className={classes.cardTitle}>⚙️ Identidade e atendimento inteligente</div>
        <div className={classes.fieldGroup}>
          <div className={classes.fieldRow}>
            <div>
              <label className={classes.fieldLabel}>Como se apresentar</label>
              <select className={classes.fieldInput}
                value={config.publicIdentityMode || "agent"}
                onChange={e => updateConfig("publicIdentityMode", e.target.value)}>
                <option value="agent">Pelo nome do agente</option>
                <option value="company">Como assistente da empresa</option>
                <option value="custom">Apresentação personalizada</option>
                <option value="none">Sem apresentação automática</option>
              </select>
            </div>
            <div>
              <label className={classes.fieldLabel}>Nome público da empresa</label>
              <input className={classes.fieldInput}
                value={config.publicCompanyName || ""}
                onChange={e => updateConfig("publicCompanyName", e.target.value)}
                placeholder="Ex.: ServParking" />
            </div>
          </div>

          <div>
            <label className={classes.fieldLabel}>Saudação preferencial</label>
            <input className={classes.fieldInput}
              value={config.openingMessage || ""}
              onChange={e => updateConfig("openingMessage", e.target.value)}
              placeholder="Olá! Tudo bem? Sou assistente da sua empresa. Como posso ajudar?" />
          </div>

          <div className={classes.fieldRow}>
            <div>
              <label className={classes.fieldLabel}>Tamanho das respostas</label>
              <select className={classes.fieldInput}
                value={config.replyLength || "standard"}
                onChange={e => updateConfig("replyLength", e.target.value)}>
                <option value="short">Curtas e objetivas</option>
                <option value="standard">Naturais, conforme o contexto</option>
                <option value="detailed">Mais explicativas quando necessário</option>
              </select>
            </div>
            <div>
              <label className={classes.fieldLabel}>Objetivo principal</label>
              <input className={classes.fieldInput}
                value={config.mainGoal || ""}
                onChange={e => updateConfig("mainGoal", e.target.value)}
                placeholder="Ex.: qualificar para cotação, agendar, reservar..." />
            </div>
          </div>
        </div>
      </div>

      <div className={classes.card}>
        <div className={classes.cardTitle}>📋 Dados para coletar no atendimento</div>
        <div className={classes.fieldGroup}>
          <div style={{ fontSize: 12, color: "#64748B" }}>
            A IA poderá reconhecer informações em qualquer ordem e perguntar o que faltar.
          </div>

          {(Array.isArray(config.qualificationFields)
            ? config.qualificationFields : []).map((field, index) => (
            <div key={index} style={{
              display: "flex", flexWrap: "wrap", alignItems: "center",
              gap: 8, padding: 10, border: "1px solid #E2E8F0",
              borderRadius: 10
            }}>
              <input className={classes.fieldInput}
                style={{ flex: "2 1 180px" }}
                placeholder="Nome do campo"
                value={field.label || ""}
                onChange={e => {
                  const next = [...(config.qualificationFields || [])];
                  next[index] = { ...field, label: e.target.value };
                  updateConfig("qualificationFields", next);
                }} />

              <select className={classes.fieldInput}
                style={{ flex: "1 1 110px" }}
                value={field.type || "text"}
                onChange={e => {
                  const next = [...(config.qualificationFields || [])];
                  next[index] = { ...field, type: e.target.value };
                  updateConfig("qualificationFields", next);
                }}>
                <option value="text">Texto</option>
                <option value="email">E-mail</option>
                <option value="phone">Telefone</option>
                <option value="date">Data</option>
                <option value="time">Horário</option>
                <option value="datetime">Data e hora</option>
                <option value="number">Número</option>
                <option value="choice">Escolha</option>
              </select>

              <label style={{ display: "flex", gap: 4, fontSize: 12 }}>
                <input type="checkbox" checked={!!field.required}
                  onChange={e => {
                    const next = [...(config.qualificationFields || [])];
                    next[index] = { ...field, required: e.target.checked };
                    updateConfig("qualificationFields", next);
                  }} />
                Obrigatório
              </label>

              <button type="button" onClick={() => updateConfig(
                "qualificationFields",
                (config.qualificationFields || []).filter((_, i) => i !== index)
              )} style={{
                border: "none", background: "transparent",
                color: "#DC2626", cursor: "pointer"
              }}>Remover</button>
            </div>
          ))}

          <button type="button" onClick={() => updateConfig(
            "qualificationFields",
            [...(config.qualificationFields || []),
              { label: "", type: "text", required: true }]
          )} style={{
            padding: 12, borderRadius: 10, border: "1px solid #0057FF",
            background: "#EFF6FF", color: "#0057FF",
            fontWeight: 700, cursor: "pointer"
          }}>
            + Adicionar informação
          </button>
        </div>
      </div>

      <div className={classes.card}>
        <div className={classes.cardTitle}>
          ✅ Finalização e ações do atendimento
        </div>

        <div className={classes.fieldGroup}>
          <div>
            <label className={classes.fieldLabel}>
              O que deve acontecer ao concluir o objetivo?
            </label>
            <select className={classes.fieldInput}
              value={config.finalAction || "none"}
              onChange={e => updateConfig("finalAction", e.target.value)}>
              <option value="none">Sem ação definida</option>
              <option value="human">Transferir para atendimento humano</option>
              <option value="schedule">Solicitar agendamento</option>
              <option value="crm">Registrar oportunidade no CRM</option>
              <option value="answer">Somente orientar o cliente</option>
            </select>
          </div>

          <div style={{
            display: "flex", flexDirection: "column",
            gap: 12, padding: "12px 0"
          }}>
            <label style={{ display: "flex", gap: 9, alignItems: "center" }}>
              <input type="checkbox"
                checked={config.finalSummary !== false}
                onChange={e => updateConfig("finalSummary", e.target.checked)}
              />
              <span>Apresentar resumo dos dados antes de finalizar</span>
            </label>

            <label style={{ display: "flex", gap: 9, alignItems: "center" }}>
              <input type="checkbox"
                checked={config.finalConfirmation !== false}
                onChange={e => updateConfig("finalConfirmation", e.target.checked)}
              />
              <span>Solicitar confirmação do cliente</span>
            </label>
          </div>

          <div>
            <label className={classes.fieldLabel}>
              Instruções adicionais de finalização
            </label>
            <textarea className={classes.fieldTextarea}
              rows={3}
              value={config.finalInstructions || ""}
              onChange={e => updateConfig("finalInstructions", e.target.value)}
              placeholder="Ex.: Após confirmar os dados, encaminhar a solicitação ao setor responsável."
            />
          </div>

          <div style={{
            fontSize: 12, color: "#64748B",
            background: "#F8FAFC", padding: 12,
            borderRadius: 10
          }}>
            Estas opções personalizam o treinamento. A execução real
            depende dos mecanismos de transferência, agenda e CRM
            configurados na Korax.
          </div>
        </div>
      </div>

      <div className={classes.card}>
        <div className={classes.cardTitle}>🧠 Conhecimento do agente</div>
        <div className={classes.fieldGroup}>
          <div>
            <label className={classes.fieldLabel}>O que o agente precisa saber sobre sua empresa?</label>
            <textarea className={classes.fieldTextarea} value={config.companyInfo}
              onChange={e => updateConfig("companyInfo", e.target.value)}
              placeholder="Ex: história, diferenciais, localização, forma de atendimento, horários, garantias, posicionamento e informações importantes."
              rows={4} />
          </div>
          <div>
            <label className={classes.fieldLabel}>O que o agente precisa saber para vender bem?</label>
            <textarea className={classes.fieldTextarea} value={config.productDetails}
              onChange={e => updateConfig("productDetails", e.target.value)}
              placeholder="Ex: benefícios, preços, planos, formas de pagamento, dúvidas comuns, diferenciais e motivos para o cliente escolher vocês."
              rows={5} />
          </div>
          <div>
            <label className={classes.fieldLabel}>Mensagem quando precisar chamar alguém da equipe</label>
            <input className={classes.fieldInput} value={config.humanTransferMessage}
              onChange={e => updateConfig("humanTransferMessage", e.target.value)}
              placeholder="Ex: Vou te conectar com um especialista agora! Um momento 😊" />
            <span style={{ fontSize: "0.7rem", color: "#94a3b8", marginTop: 4, display: "block" }}>Essa mensagem será usada quando o lead pedir atendimento humano ou quando o fluxo mandar transferir.</span>
          </div>
        </div>
      </div>
    </div>
  );

  
const renderSmartMode = () => (
  <div className={classes.contentInner}>
    <div
      style={{
        background: "#fff",
        border: "1px solid #E2E8F0",
        borderRadius: 24,
        padding: 32,
        boxShadow: "0 18px 45px rgba(15,23,42,.06)",
        maxWidth: 920,
        margin: "0 auto"
      }}
    >

      <div
        style={{
          textAlign: "center",
          marginBottom: 30
        }}
      >

        <div style={{ fontSize: 58 }}>
          🤖
        </div>

        <div
          style={{
            fontSize: 28,
            fontWeight: 900,
            color: "#0F172A",
            marginTop: 12
          }}
        >
          Atendimento Inteligente
        </div>

        <div
          style={{
            marginTop: 10,
            color: "#64748B",
            lineHeight: 1.7,
            maxWidth: 650,
            marginLeft: "auto",
            marginRight: "auto"
          }}
        >
          Sua IA responderá naturalmente utilizando todo o conhecimento
          cadastrado sobre sua empresa.

          Não existe uma sequência obrigatória de etapas.
        </div>

      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
          gap: 14,
          marginBottom: 28
        }}
      >

        {[
          "Responder dúvidas",
          "Explicar produtos",
          "Usar a base de conhecimento",
          "Transferir para um humano"
        ].map(item => (

          <div
            key={item}
            style={{
              border: "1px solid #E2E8F0",
              borderRadius: 16,
              padding: 18,
              background: "#F8FAFC",
              fontWeight: 700,
              color: "#334155"
            }}
          >
            ✅ {item}
          </div>

        ))}

      </div>

      <div
        style={{
          background: "#EFF6FF",
          border: "1px solid #BFDBFE",
          borderRadius: 20,
          padding: 24,
          textAlign: "center"
        }}
      >

        <div
          style={{
            fontWeight: 900,
            fontSize: 18,
            color: "#0F172A"
          }}
        >
          Precisa vender seguindo um processo?
        </div>

        <div
          style={{
            color: "#64748B",
            marginTop: 8,
            marginBottom: 22
          }}
        >
          Ative a Jornada Comercial para utilizar etapas,
          perguntas obrigatórias,
          mídias e follow-ups.
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 18,
            marginTop: 25
          }}
        >

          {!directJourney && (
          <div
            onClick={()=>{
              updateConfig("useJourney",false);
              setSelectedExperience("smart");
            }}
            style={{
              cursor:"pointer",
              border:"2px solid #22C55E",
              borderRadius:18,
              padding:20,
              background:"#F0FDF4"
            }}
          >
            <div style={{fontWeight:900,fontSize:17}}>
              🤖 Atendimento Inteligente
            </div>

            <div style={{marginTop:12,fontSize:13,lineHeight:1.8,color:"#475569"}}>
              ✓ Conversa natural<br/>
              ✓ Responde dúvidas<br/>
              ✓ Aprende sobre a empresa<br/>
              ✓ Ideal para suporte e atendimento
            </div>

          </div>
          )}

          <div
            onClick={()=>{
  updateConfig("useJourney",true);
  setSelectedExperience("journey");
}}
            style={{
              cursor:"pointer",
              border:"2px solid #0066FF",
              borderRadius:18,
              padding:20,
              background:"#EFF6FF"
            }}
          >
            <div style={{fontWeight:900,fontSize:17}}>
              🚀 Jornada Comercial
            </div>

            <div style={{marginTop:12,fontSize:13,lineHeight:1.8,color:"#334155"}}>
              ✓ Qualificação automática<br/>
              ✓ Etapas de venda<br/>
              ✓ Follow-up automático<br/>
              ✓ Ideal para operações comerciais
            </div>

            <div
              style={{
                marginTop:18,
                background:"#0066FF",
                color:"#fff",
                borderRadius:10,
                padding:"10px",
                textAlign:"center",
                fontWeight:900
              }}
            >
              Ativar Jornada Comercial
            </div>

          </div>

        </div>

      </div>

    </div>
  </div>
);


const renderJornada = () => {
    const selectedStep = steps.find(s => s.id === selectedJourneyStepId) || steps[0];
    const selectedIndex = Math.max(0, steps.findIndex(s => s.id === selectedStep?.id));
    const step = selectedStep || steps[0];
    const index = selectedIndex;
    const stepType = STEP_TYPES.find(s => s.id === step?.type) || STEP_TYPES[7];
    const color = STEP_COLORS[index % STEP_COLORS.length];
    const valid = step ? stepValid(step) : false;
    const sugs = SUGESTOES[step?.type] || SUGESTOES.custom;
    const automation = step?.automation || getDefaultAutomation();
    const activeFollowUps = steps.reduce((acc, s) => acc + (((s.automation?.followUps || []).filter(f => f.enabled).length) || 0), 0);
    const totalMedias = steps.reduce((acc, s) => acc + ((s.medias || []).length), 0);

    if (!step) {
      return (
        <div style={{ padding: 30, textAlign: "center", color: "#64748B" }}>
          Nenhuma etapa criada ainda.
        </div>
      );
    }

    return (
      <div className={classes.builderV2Shell}>
        <aside>
          <div className={classes.builderV2Panel}>
            <div className={classes.builderV2PanelPad}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <div>
                  <div style={{ fontWeight: 950, fontSize: 16, color: "#0F172A" }}>Jornada do Lead</div>
                  <div style={{ fontSize: 12, color: "#64748B", marginTop: 3 }}>Organize o caminho que sua IA deve seguir.</div>
                </div>
                <span style={{ background: "#EFF6FF", color: "#0066FF", borderRadius: 999, padding: "5px 9px", fontSize: 11, fontWeight: 900 }}>
                  {steps.length} etapas
                </span>
              </div>

              <button
                onClick={() => setTypePickerOpen(true)}
                style={{
                  width: "100%",
                  border: "none",
                  borderRadius: 15,
                  padding: "12px 14px",
                  background: "linear-gradient(135deg,#0066FF,#00A3FF)",
                  color: "#fff",
                  fontWeight: 900,
                  fontSize: 13,
                  cursor: "pointer",
                  marginBottom: 14,
                  boxShadow: "0 12px 24px rgba(0,102,255,.22)"
                }}
              >
                + Adicionar etapa
              </button>

              {typePickerOpen && (
                <div style={{ border: "1px solid #E2E8F0", borderRadius: 16, padding: 10, marginBottom: 14, background: "#F8FAFC" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 900, color: "#0F172A" }}>Nova etapa</span>
                    <button onClick={() => setTypePickerOpen(false)} style={{ border: "none", background: "transparent", cursor: "pointer", color: "#94A3B8", fontSize: 18 }}>×</button>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 7 }}>
                    {STEP_TYPES.map(t => (
                      <div key={t.id} onClick={() => addStep(t.id)}
                        style={{ display: "flex", alignItems: "center", gap: 9, padding: "9px 10px", borderRadius: 12, border: "1px solid #E2E8F0", cursor: "pointer", background: "#fff" }}>
                        <span>{t.icon}</span>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 900, color: "#0F172A" }}>{t.label}</div>
                          <div style={{ fontSize: 10.5, color: "#64748B" }}>{t.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                {steps.map((s, i) => {
                  const t = STEP_TYPES.find(tp => tp.id === s.type) || STEP_TYPES[7];
                  const c = STEP_COLORS[i % STEP_COLORS.length];
                  const isActive = s.id === step.id;
                  return (
                    <div
                      key={s.id}
                      className={`${classes.journeyMiniCard} ${isActive ? classes.journeyMiniCardActive : ""}`}
                      onClick={() => setSelectedJourneyStepId(s.id)}
                    >
                      <div style={{ width: 26, height: 26, borderRadius: 10, background: isActive ? c : "#F1F5F9", color: isActive ? "#fff" : "#64748B", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 950, flexShrink: 0 }}>
                        {i + 1}
                      </div>
                      <div style={{ width: 32, height: 32, borderRadius: 13, background: `${c}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {t.icon}
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 950, color: "#0F172A", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.title}</div>
                        <div style={{ fontSize: 10.8, color: "#64748B", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.desc}</div>
                      </div>
                      <span style={{ fontSize: 14, color: "#94A3B8" }}>⋮</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className={classes.builderV2Panel} style={{ marginTop: 14 }}>
            <div className={classes.builderV2PanelPad}>
              <div style={{ fontWeight: 950, fontSize: 14, color: "#0F172A", marginBottom: 14 }}>Resumo da Jornada</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8 }}>
                <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 15, padding: 10, textAlign: "center" }}>
                  <div style={{ fontSize: 18, fontWeight: 950, color: "#0066FF" }}>{steps.length}</div>
                  <div style={{ fontSize: 10.5, color: "#64748B", fontWeight: 800 }}>Etapas</div>
                </div>
                <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 15, padding: 10, textAlign: "center" }}>
                  <div style={{ fontSize: 18, fontWeight: 950, color: "#16A34A" }}>{steps.filter(stepValid).length}</div>
                  <div style={{ fontSize: 10.5, color: "#64748B", fontWeight: 800 }}>Completas</div>
                </div>
                <div style={{ background: "#FAF5FF", border: "1px solid #E9D5FF", borderRadius: 15, padding: 10, textAlign: "center" }}>
                  <div style={{ fontSize: 18, fontWeight: 950, color: "#7C3AED" }}>{activeFollowUps}</div>
                  <div style={{ fontSize: 10.5, color: "#64748B", fontWeight: 800 }}>Follow-ups</div>
                </div>
              </div>
              <div style={{ marginTop: 12, borderRadius: 15, padding: 12, background: "linear-gradient(135deg,#ECFDF5,#F0FDF4)", border: "1px solid #BBF7D0", color: "#15803D", fontWeight: 850, fontSize: 12 }}>
                {steps.every(stepValid) ? "✅ Jornada bem configurada!" : "⚠️ Existem etapas incompletas"}
              </div>
            </div>
          </div>
        </aside>

        <main className={classes.builderV2Editor}>
          <div style={{ padding: 18, borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 13, minWidth: 0 }}>
              <div style={{ width: 54, height: 54, borderRadius: 18, background: `linear-gradient(135deg, ${color}, #00A3FF)`, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 23, boxShadow: "0 12px 24px rgba(0,102,255,.20)" }}>
                {stepType.icon}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 11, color: "#64748B", fontWeight: 800 }}>Editando etapa {index + 1} de {steps.length}</div>
                <input
                  value={step.title}
                  onChange={e => updateStep(step.id, "title", e.target.value)}
                  style={{ border: "none", outline: "none", background: "transparent", fontWeight: 950, fontSize: 24, color: "#0F172A", width: "100%", fontFamily: "inherit", padding: 0, marginTop: 2 }}
                />
                <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>{stepType.desc}</div>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div className={classes.validBadge} style={{ background: valid ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.10)", color: valid ? "#16A34A" : "#DC2626", padding: "7px 10px" }}>
                {valid ? "✓ Etapa completa" : "⚠ Incompleta"}
              </div>
              {steps.length > 1 && (
                <IconButton size="small" onClick={() => removeStep(step.id)} style={{ color: "#EF4444", background: "rgba(239,68,68,.08)" }}>
                  <DeleteOutlineIcon style={{ fontSize: 17 }} />
                </IconButton>
              )}
            </div>
          </div>

          <div style={{ padding: 18 }}>
            <div style={{
              border: "1px solid #DBEAFE",
              background: "linear-gradient(135deg,#FFFFFF,#F8FBFF)",
              borderRadius: 20,
              padding: 16,
              marginBottom: 16
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{
                  width: 42,
                  height: 42,
                  borderRadius: 16,
                  background: "linear-gradient(135deg,#0066FF,#00A3FF)",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 20,
                  boxShadow: "0 12px 24px rgba(0,102,255,.18)"
                }}>🧩</div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 950, color: "#0F172A" }}>Construtor visual da etapa</div>
                  <div style={{ fontSize: 12, color: "#64748B", marginTop: 3 }}>
                    Ensine o que a IA precisa alcançar, considerar e descobrir nesta etapa. A fala será adaptada ao contexto da conversa.
                  </div>
                </div>
              </div>
            </div>

            {true && (
              <>
                <div style={{
                  display: "grid",
                  gridTemplateColumns: window.innerWidth <= 768 ? "1fr" : "1fr 1fr",
                  gap: 14
                }}>
                  <div style={{ border: "1px solid #E2E8F0", borderRadius: 18, padding: 15, background: "#FFFFFF" }}>
                    <label className={classes.fieldLabel}>🎯 Objetivo da etapa *</label>
                    <textarea
                      value={step.objective || ""}
                      onChange={e => updateStep(step.id, "objective", e.target.value)}
                      placeholder="Ex: Entender como o cliente utiliza o WhatsApp hoje."
                      rows={window.innerWidth <= 768 ? 5 : 6}
                      style={{
                        width: "100%",
                        padding: "12px 14px",
                        borderRadius: 14,
                        border: `1px solid ${(step.objective || "").trim() ? "#E2E8F0" : "#FCA5A5"}`,
                        fontSize: "0.86rem",
                        outline: "none",
                        boxSizing: "border-box",
                        background: "#FAFBFF",
                        fontFamily: "inherit",
                        resize: "vertical",
                        lineHeight: 1.6
                      }}
                    />
                    <div style={{ fontSize: 11, color: "#64748B", marginTop: 8 }}>
                      Defina o resultado que esta etapa precisa alcançar. Não escreva uma fala pronta.
                    </div>
                  </div>

                  <div style={{ border: "1px solid #E2E8F0", borderRadius: 18, padding: 15, background: "#FFFFFF" }}>
                    <label className={classes.fieldLabel}>🔎 Informação a descobrir *</label>
                    <textarea
                      value={step.finalQuestion}
                      onChange={e => updateStep(step.id, "finalQuestion", e.target.value)}
                      placeholder="Ex: Hoje vocês usam o WhatsApp mais para atendimento, vendas ou agendamentos?"
                      rows={window.innerWidth <= 768 ? 5 : 6}
                      style={{
                        width: "100%",
                        padding: "12px 14px",
                        borderRadius: 14,
                        border: `1px solid ${step.finalQuestion ? "#0066FF33" : "#FCA5A5"}`,
                        fontSize: "0.86rem",
                        outline: "none",
                        boxSizing: "border-box",
                        background: step.finalQuestion ? "rgba(0,102,255,0.02)" : "#FFF5F5",
                        fontFamily: "inherit",
                        resize: "vertical",
                        lineHeight: 1.6
                      }}
                    />
                    <div style={{ fontSize: 11, color: "#64748B", marginTop: 8 }}>
                      Esta é uma pergunta de referência. A IA pode reformular naturalmente e não deve repeti-la se a informação já estiver clara.
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 14, border: "1px solid #E2E8F0", borderRadius: 18, padding: 15, background: "#FFFFFF" }}>
                  <label className={classes.fieldLabel}>
                    {index === 0 ? (
                      <>💬 Mensagem inicial *</>
                    ) : (
                      <>🧠 Contexto / orientação <span style={{ color: "#94A3B8", fontWeight: 700 }}>(opcional)</span></>
                    )}
                  </label>

                  <textarea
                    value={step.message || ""}
                    onChange={e => updateStep(step.id, "message", e.target.value)}
                    placeholder={
                      index === 0
                        ? "Escreva a mensagem real que o cliente receberá ao iniciar a conversa."
                        : "Ex: Conecte a próxima pergunta ao segmento e ao que o cliente acabou de informar. Evite confirmações genéricas."
                    }
                    rows={window.innerWidth <= 768 ? 6 : 5}
                    style={{
                      width: "100%",
                      padding: "12px 14px",
                      borderRadius: 14,
                      border: `1px solid ${
                        index === 0 && !(step.message || "").trim()
                          ? "#FCA5A5"
                          : "#E2E8F0"
                      }`,
                      fontSize: "0.86rem",
                      outline: "none",
                      boxSizing: "border-box",
                      background: "#FAFBFF",
                      fontFamily: "inherit",
                      resize: "vertical",
                      lineHeight: 1.6
                    }}
                  />

                  <div style={{ fontSize: 11, color: "#64748B", marginTop: 8 }}>
                    {index === 0
                      ? "A primeira etapa é especial: esta mensagem será enviada diretamente ao cliente antes da primeira pergunta."
                      : "Oriente o comportamento da IA nesta etapa. Este conteúdo não é uma mensagem pronta para ser copiada."}
                  </div>

                  <button className={classes.suggestBtn} onClick={() => updateStep(step.id, "showSuggest", !step.showSuggest)}>
                    <LightbulbOutlinedIcon style={{ fontSize: 14 }} />
                    {index === 0
                      ? ` Sugestões de abertura`
                      : ` Sugestões de orientação para ${stepType.label}`}
                  </button>

                  {step.showSuggest && (
                    <div className={classes.suggestBox}>
                      {sugs.mensagens.map((s, i) => (
                        <div key={i} className={classes.suggestItem}
                          onClick={() => { updateStep(step.id, "message", s); updateStep(step.id, "showSuggest", false); }}>
                          {s}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {true && (
            <div style={{ marginTop: 14, border: "1px solid #E2E8F0", borderRadius: 18, padding: 15, background: "#FFFFFF" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 12 }}>
                <div>
                  <div style={{ fontWeight: 950, color: "#0F172A", fontSize: 14 }}>📎 Mídias desta etapa</div>
                  <div style={{ color: "#64748B", fontSize: 12, marginTop: 3 }}>Imagens, vídeos, áudios e documentos que a IA pode usar.</div>
                </div>
                <button onClick={() => { setMediaTargetStep(step.id); setMediaModalOpen(true); }}
                  style={{ borderRadius: 12, border: "1px solid rgba(0,102,255,0.25)", background: "rgba(0,102,255,0.06)", color: "#0066FF", padding: "8px 12px", fontSize: "0.75rem", fontWeight: 900, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }}>
                  <AddIcon style={{ fontSize: 15 }} /> Adicionar mídia
                </button>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))", gap: 10 }}>
                {(step.medias || []).map(m => {
                  const tc = TYPE_CONFIG[m.type] || TYPE_CONFIG.image;
                  return (
                    <div key={m.id} style={{ border: `1px solid ${tc.color}22`, background: tc.bg, borderRadius: 15, padding: 12, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 18 }}>{tc.emoji}</div>
                        <div style={{ fontSize: 12, fontWeight: 900, color: tc.color, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.name}</div>
                        <div style={{ fontSize: 10.5, color: "#64748B", fontWeight: 800 }}>{tc.label}</div>
                      </div>
                      <button onClick={() => removeMediaFromStep(step.id, m.id)} style={{ border: "none", background: "rgba(255,255,255,.65)", color: "#EF4444", borderRadius: 8, cursor: "pointer", padding: "4px 7px", fontWeight: 950 }}>×</button>
                    </div>
                  );
                })}

                {!(step.medias || []).length && (
                  <div style={{ gridColumn: "1 / -1", border: "1.5px dashed #CBD5E1", background: "#F8FAFC", borderRadius: 16, padding: 18, color: "#64748B", fontSize: 12, fontWeight: 800, textAlign: "center" }}>
                    Nenhuma mídia adicionada nesta etapa.
                  </div>
                )}
              </div>
            </div>
            )}

            {true && (
            <div style={{ marginTop: 14, borderRadius: 20, border: "1px solid rgba(124,58,237,0.22)", background: "linear-gradient(135deg, rgba(0,102,255,0.045), rgba(124,58,237,0.055))", padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 14 }}>
                <div>
                  <div style={{ fontWeight: 950, fontSize: 15, color: "#0F172A" }}>⚡ Automações da etapa</div>
                  <div style={{ fontSize: 12, color: "#64748B", marginTop: 4 }}>Ative lembretes automáticos se o lead parar de responder nesta etapa.</div>
                </div>

                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 900, color: automation.enabled ? "#0066FF" : "#64748B", cursor: "pointer", whiteSpace: "nowrap" }}>
                  <input type="checkbox" checked={!!automation.enabled} onChange={e => updateStepAutomation(step.id, a => ({ ...a, enabled: e.target.checked }))} />
                  Ativar automação
                </label>
              </div>

              <div style={{
                display: "grid",
                gridTemplateColumns: window.innerWidth <= 768 ? "1fr" : "repeat(3,1fr)",
                gap: 12
              }}>
                {(automation.followUps || getDefaultAutomation().followUps).map((fu, fuIndex) => (
                  <div key={fu.id} style={{ borderRadius: 16, border: "1px solid #E2E8F0", background: "#fff", padding: 13, opacity: automation.enabled ? 1 : .58 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 10 }}>
                      <div style={{ fontWeight: 950, fontSize: 12, color: "#0F172A" }}>Follow-up {fuIndex + 1}</div>
                      <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 850, color: "#64748B" }}>
                        <input type="checkbox" checked={!!fu.enabled} disabled={!automation.enabled} onChange={e => updateStepFollowUp(step.id, fu.id, "enabled", e.target.checked)} />
                        Usar
                      </label>
                    </div>

                    <label className={classes.fieldLabel}>Aguardar</label>
                    <div style={{ display: "grid", gridTemplateColumns: "70px 1fr", gap: 7, marginBottom: 9 }}>
                      <input type="number" min="1" value={fu.delay} disabled={!automation.enabled || !fu.enabled}
                        onChange={e => updateStepFollowUp(step.id, fu.id, "delay", Number(e.target.value || 1))}
                        style={{ width: "100%", padding: "8px 9px", borderRadius: 10, border: "1px solid #E2E8F0", fontSize: 12, outline: "none", boxSizing: "border-box" }} />
                      <select value={fu.unit} disabled={!automation.enabled || !fu.enabled}
                        onChange={e => updateStepFollowUp(step.id, fu.id, "unit", e.target.value)}
                        style={{ width: "100%", padding: "8px 9px", borderRadius: 10, border: "1px solid #E2E8F0", fontSize: 12, outline: "none", background: "#F8FAFC", color: "#334155", boxSizing: "border-box" }}>
                        <option value="minutes">minutos</option>
                        <option value="hours">horas</option>
                        <option value="days">dias</option>
                      </select>
                    </div>

                    <label className={classes.fieldLabel}>Mensagem personalizada</label>
                    <textarea value={fu.message} disabled={!automation.enabled || !fu.enabled}
                      onChange={e => updateStepFollowUp(step.id, fu.id, "message", e.target.value)}
                      placeholder="Mensagem caso o lead não responda..."
                      rows={3}
                      style={{ width: "100%", padding: "9px 10px", borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12, outline: "none", boxSizing: "border-box", background: "#FAFBFF", fontFamily: "inherit", resize: "vertical", lineHeight: 1.5 }}
                    />
                  </div>
                ))}


              </div>
            </div>
            )}

            {true && (
              <div style={{ marginTop: 14, borderRadius: 22, border: "1px solid rgba(0,102,255,0.20)", background: "linear-gradient(135deg,#FFFFFF,#F8FAFC)", padding: 20 }}>
                <div style={{ marginBottom: 18 }}>
                  <div style={{ fontWeight: 950, fontSize: 17, color: "#0F172A" }}>➡️ Depois da resposta</div>
                  <div style={{ fontSize: 12, color: "#64748B", marginTop: 4 }}>
                    Escolha o próximo passo automático quando o lead responder esta etapa.
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: window.innerWidth <= 768 ? "1fr" : "repeat(3,1fr)", gap: 10 }}>
                  {[
                    ["next", "➡️", "Próxima etapa", "Continua a jornada automaticamente."],
                    ["catalog", "🛍️", "Mostrar catálogo", "Abre o catálogo, aguarda a escolha do cliente e depois continua o atendimento."],
                    ["agenda", "📅", "Abrir agenda", "Mostra os horários disponíveis, agenda o atendimento e continua a conversa."],
                    ["human", "🙋", "Transferir equipe", "Entrega a conversa para um atendente da equipe."],
                    ["finish", "✅", "Encerrar", "Finaliza o atendimento da IA."],
                    ["custom", "💬", "Mensagem", "Envia uma resposta personalizada antes de continuar o fluxo."]
                  ].map(([value, icon, title, desc]) => {
                    const active = ((step.flow && step.flow.action) || "next") === value;
                    return (
                      <div
                        key={value}
                        onClick={() => updateStepFlow(step.id, "action", value)}
                        style={{
                          border: `1.5px solid ${active ? "#0066FF" : "#E2E8F0"}`,
                          background: active ? "linear-gradient(135deg,#EFF6FF,#FFFFFF)" : "#FFFFFF",
                          borderRadius: 18,
                          padding: 14,
                          cursor: "pointer",
                          boxShadow: active ? "0 14px 28px rgba(0,102,255,.12)" : "none"
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                          <div style={{ width: 34, height: 34, borderRadius: 13, background: active ? "#0066FF" : "#F1F5F9", color: active ? "#fff" : "#64748B", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>
                            {icon}
                          </div>
                          <div style={{ fontSize: 13, fontWeight: 950, color: "#0F172A" }}>{title}</div>
                        </div>
                        <div style={{ fontSize: 11.5, color: "#64748B", lineHeight: 1.45 }}>{desc}</div>
                      </div>
                    );
                  })}
                </div>

                {((step.flow && step.flow.action) || "next") !== "next" && (
                  <div style={{ marginTop: 18, borderTop: "1px solid #E2E8F0", paddingTop: 16 }}>
                    <label className={classes.fieldLabel}>Mensagem opcional antes da ação</label>\n                    <div style={{ fontSize: 11, color: "#64748B", marginBottom: 8 }}>Se preenchida, esta mensagem será enviada antes de executar a ação escolhida.</div>
                    <textarea
                      value={(step.flow && step.flow.message) || ""}
                      onChange={e => updateStepFlow(step.id, "message", e.target.value)}
                      placeholder="Ex: Perfeito, vou te mostrar as melhores opções agora."
                      rows={3}
                      style={{ width: "100%", padding: "12px 14px", borderRadius: 14, border: "1px solid #E2E8F0", background: "#FAFBFF", fontFamily: "inherit", resize: "vertical", lineHeight: 1.5 }}
                    />
                  </div>
                )}

                {((step.flow && step.flow.action) || "next") === "catalog" && (
                  <div style={{ marginTop: 16, borderRadius: 18, border: "1px solid #DBEAFE", background: "#F8FBFF", padding: 16 }}>
                    <div style={{ fontWeight: 950, color: "#0F172A", fontSize: 14, marginBottom: 4 }}>🛍️ Quando o cliente escolher um item</div>
                    <div style={{ fontSize: 12, color: "#64748B", marginBottom: 12 }}>Defina o próximo passo depois da escolha no catálogo.</div>

                    <select
                      value={(step.flow && step.flow.afterCatalogChoice) || "continue"}
                      onChange={e => updateStepFlow(step.id, "afterCatalogChoice", e.target.value)}
                      style={{ width: "100%", padding: "12px 14px", borderRadius: 14, border: "1px solid #E2E8F0", background: "#FFFFFF", fontWeight: 850, color: "#0F172A", outline: "none", marginBottom: 12 }}
                    >
                      <option value="continue">Continuar conversa pelo WhatsApp</option>
                      <option value="ask_question">Fazer uma pergunta</option>
                      <option value="send_link">Enviar link de compra/pagamento</option>
                      <option value="agenda">Abrir agenda</option>
                      <option value="human">Transferir para equipe</option>
                      <option value="finish">Encerrar atendimento</option>
                    </select>

                    {((step.flow && step.flow.afterCatalogChoice) || "continue") !== "continue" && (
                      <>
                        <label className={classes.fieldLabel}>Mensagem pós-escolha</label>
                        <textarea
                          value={(step.flow && step.flow.afterCatalogMessage) || ""}
                          onChange={e => updateStepFlow(step.id, "afterCatalogMessage", e.target.value)}
                          placeholder="Ex: Perfeito. Para seguir, me informe seu CEP."
                          rows={3}
                          style={{ width: "100%", padding: "12px 14px", borderRadius: 14, border: "1px solid #E2E8F0", background: "#FFFFFF", fontFamily: "inherit", resize: "vertical", lineHeight: 1.5, marginBottom: 12 }}
                        />
                      </>
                    )}

                    {((step.flow && step.flow.afterCatalogChoice) || "continue") === "send_link" && (
                      <>
                        <label className={classes.fieldLabel}>Link de compra/pagamento</label>
                        <input
                          value={(step.flow && step.flow.afterCatalogLink) || ""}
                          onChange={e => updateStepFlow(step.id, "afterCatalogLink", e.target.value)}
                          placeholder="https://..."
                          style={{ width: "100%", padding: "12px 14px", borderRadius: 14, border: "1px solid #E2E8F0", background: "#FFFFFF", boxSizing: "border-box" }}
                        />
                      </>
                    )}
                  </div>
                )}

                {((step.flow && step.flow.action) || "next") === "custom" && (
                  <div style={{ marginTop: 16 }}>
                    <label className={classes.fieldLabel}>Link opcional</label>
                    <input
                      value={(step.flow && step.flow.link) || ""}
                      onChange={e => updateStepFlow(step.id, "link", e.target.value)}
                      placeholder="https://..."
                      style={{ width: "100%", padding: "12px 14px", borderRadius: 14, border: "1px solid #E2E8F0", background: "#FFFFFF", boxSizing: "border-box" }}
                    />
                  </div>
                )}
              </div>
            )}
            )}
          </div>
        </main>

      </div>
    );
  };

  const renderObjecoes = () => (
    <div className={classes.contentInner}>
      <div className={classes.card}>
        <div className={classes.cardTitle}>🛡️ Banco de Objeções</div>
        <div style={{ fontSize: "0.8rem", color: "#64748B", marginBottom: 16, lineHeight: 1.6 }}>
          Cadastre as objeções mais comuns dos seus clientes e como a IA deve responder. Quanto mais completo, melhor a IA vai contornar resistências.
        </div>
        <div className={classes.fieldGroup}>
          <div>
            <label className={classes.fieldLabel}>Objeção do cliente</label>
            <input className={classes.fieldInput} value={objectionForm.objection}
              onChange={e => setObjectionForm(f => ({ ...f, objection: e.target.value }))}
              placeholder='Ex: "Está muito caro", "Preciso pensar", "Já tenho outra solução"' />
          </div>
          <div>
            <label className={classes.fieldLabel}>Como a IA deve responder</label>
            <textarea className={classes.fieldTextarea} value={objectionForm.answer}
              onChange={e => setObjectionForm(f => ({ ...f, answer: e.target.value }))}
              placeholder="Explique a estratégia de resposta. Ex: Demonstrar empatia, mostrar o valor, dar um exemplo de cliente similar que tinha a mesma dúvida..."
              rows={3} />
          </div>
          <button onClick={addObjection}
            style={{ padding: "10px", borderRadius: 10, background: "linear-gradient(135deg,#0066FF,#00A3FF)", color: "#fff", border: "none", fontWeight: 700, fontSize: "0.84rem", cursor: "pointer" }}>
            + Adicionar objeção
          </button>
        </div>
      </div>

      {objections.length > 0 && (
        <div className={classes.card}>
          <div className={classes.cardTitle}>📋 Objeções cadastradas ({objections.length})</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {objections.map((item, i) => (
              <div key={item.id} className={classes.objectionCard}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: "0.82rem", color: "#EF4444", marginBottom: 6 }}>
                      🚫 {item.objection}
                    </div>
                    <div style={{ fontSize: "0.78rem", color: "#475569", lineHeight: 1.6 }}>
                      ✅ {item.answer}
                    </div>
                  </div>
                  <button onClick={() => removeObjection(item.id)}
                    style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", color: "#EF4444", borderRadius: 8, padding: "4px 10px", fontSize: "0.72rem", cursor: "pointer", fontWeight: 700, flexShrink: 0 }}>
                    Remover
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {objections.length === 0 && (
        <div style={{ textAlign: "center", padding: "32px 20px", color: "#94A3B8" }}>
          <div style={{ fontSize: "2.5rem", marginBottom: 8 }}>🛡️</div>
          <div style={{ fontWeight: 600, fontSize: "0.88rem", marginBottom: 4 }}>Nenhuma objeção cadastrada ainda</div>
          <div style={{ fontSize: "0.78rem" }}>Adicione as resistências mais comuns dos seus clientes acima</div>
        </div>
      )}
    </div>
  );

  const splitIntoBlocks = (text) => {
    return String(text || "")
      .split(/\n\s*\n/g)
      .map(t => t.trim())
      .filter(Boolean);
  };

  const mediaLabel = (media) => {
    const type = media?.type || "image";
    if (type === "audio") return "🎤 Áudio";
    if (type === "video") return "🎥 Vídeo";
    if (type === "pdf") return "📄 PDF";
    return "🖼️ Imagem";
  };

  const buildPreviewFlow = () => {
    const flow = [];

    steps.forEach((step, index) => {
      const stepType = STEP_TYPES.find(s => s.id === step.type) || STEP_TYPES[7];

      flow.push({
        type: "stage",
        label: `${stepType.icon} Etapa ${index + 1}: ${step.title}`
      });

      splitIntoBlocks(step.message).forEach(block => {
        flow.push({ type: "ia", text: block });
      });

      (step.medias || []).forEach(media => {
        flow.push({ type: "media", media });
      });

      if (step.finalQuestion) {
        flow.push({ type: "ia", text: step.finalQuestion, question: true });
      }

      const activeFollowUps = (step.automation?.followUps || []).filter(f => step.automation?.enabled && f.enabled && f.message);
      if (activeFollowUps.length) {
        flow.push({ type: "wait", text: "Se o lead não responder nesta etapa..." });
        activeFollowUps.forEach(fu => {
          const unitLabel = fu.unit === "minutes" ? "minutos" : fu.unit === "hours" ? "horas" : "dias";
          flow.push({ type: "followupTime", text: `${fu.delay} ${unitLabel} depois` });
          splitIntoBlocks(fu.message).forEach(block => {
            flow.push({ type: "ia", text: block, followup: true });
          });
        });
      }

      if (index < steps.length - 1) {
        flow.push({
          type: "lead",
          text: step.type === "abertura"
            ? "Oi, quero saber mais."
            : step.type === "qualificacao"
              ? "Hoje atendo vários clientes pelo WhatsApp."
              : "Faz sentido, pode me explicar melhor."
        });
      }
    });

    return flow;
  };

  const handleLabSend = async () => {
    const message = labInput.trim();
    if (!message || labLoading) return;

    const userMsg = { role: "user", content: message };
    setLabMessages(prev => [...prev, userMsg]);
    setLabInput("");
    setLabLoading(true);

    try {
      const prompt = generatePrompt();
      const history = labMessages.slice(-8).map(m => ({
        role: m.role,
        content: m.content
      }));

      const { data } = await api.post("/prompt/test", {
        prompt,
        message,
        history,
        apiKey: config.apiKey || "",
        model: config.model || "gpt-4o-mini",
        maxTokens: config.maxTokens || 500
      });

      setLabMessages(prev => [...prev, { role: "assistant", content: data.reply || "Não consegui gerar uma resposta agora." }]);
    } catch (err) {
      toast.error("Erro ao testar a IA. Verifique a API Key ou tente novamente.");
    }

    setLabLoading(false);
  };

  
  const renderSmartPreview = () => {
    return (
      <div style={{maxWidth:1100,margin:"0 auto"}}>
        <div style={{
          background:"#fff",
          border:"1px solid #E2E8F0",
          borderRadius:24,
          padding:28,
          boxShadow:"0 18px 45px rgba(15,23,42,.06)"
        }}>

          <div style={{fontSize:28,fontWeight:900,color:"#0F172A"}}>
            🤖 Atendimento Inteligente
          </div>

          <div style={{marginTop:8,color:"#64748B"}}>
            Sua IA responderá utilizando exclusivamente as informações treinadas.
          </div>

          <div style={{
            display:"grid",
            gridTemplateColumns:"1fr 1fr",
            gap:18,
            marginTop:28
          }}>

            <div style={{padding:20,border:"1px solid #E2E8F0",borderRadius:18}}>
              <div style={{fontWeight:900}}>Empresa</div>
              <div style={{marginTop:8,color:"#64748B"}}>
                {config.companyInfo || "Nenhuma informação cadastrada."}
              </div>
            </div>

            <div style={{padding:20,border:"1px solid #E2E8F0",borderRadius:18}}>
              <div style={{fontWeight:900}}>Produtos e Serviços</div>
              <div style={{marginTop:8,color:"#64748B"}}>
                {config.productDetails || "Nenhuma informação cadastrada."}
              </div>
            </div>

            <div style={{padding:20,border:"1px solid #E2E8F0",borderRadius:18}}>
              <div style={{fontWeight:900}}>Tom de voz</div>
              <div style={{marginTop:8,color:"#64748B"}}>
                {config.tone || "Natural"}
              </div>
            </div>

            <div style={{padding:20,border:"1px solid #E2E8F0",borderRadius:18}}>
              <div style={{fontWeight:900}}>Transferência</div>
              <div style={{marginTop:8,color:"#64748B"}}>
                {config.humanTransferMessage || "Mensagem padrão"}
              </div>
            </div>

          </div>

        </div>
      </div>
    );
  };



  const renderPreview = () => {
    const previewFlow = buildPreviewFlow();
    const stats = isSmartMode
      ? [
          { label: "Empresa", value: config.companyInfo ? "✓" : "—", color:"#0066FF"},
          { label: "Produtos", value: config.productDetails ? "✓" : "—", color:"#7C3AED"},
          { label: "Tom", value: config.tone || "Padrão", color:"#00A86B"},
          { label: "IA", value:"Pronta", color:"#F59E0B"}
        ]
      : [
          { label: "Etapas", value: steps.length, color: "#0066FF" },
          { label: "Mídias", value: steps.reduce((acc, s) => acc + ((s.medias || []).length), 0), color: "#7C3AED" },
          { label: "Follow-ups", value: steps.reduce((acc, s) => acc + (((s.automation?.followUps || []).filter(f => s.automation?.enabled && f.enabled).length) || 0), 0), color: "#00A86B" },
          { label: "Score", value: `${trainingScore}%`, color: "#F59E0B" }
        ];

    return (
      <div style={{ width: "100%", maxWidth: 1280, margin: "0 auto", display: "grid", gridTemplateColumns: "minmax(0,1fr) 360px", gap: 18 }}>
        <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 24, overflow: "hidden", boxShadow: "0 18px 45px rgba(15,23,42,.06)" }}>
          <div style={{ padding: 18, borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <div>
              <div style={{ fontSize: 22, fontWeight: 950, color: "#0F172A", letterSpacing: -0.5 }}>Laboratório da IA Korax</div>
              <div style={{ color: "#64748B", fontSize: 13, marginTop: 4 }}>
                Veja como o atendimento será enviado em blocos, mídias e follow-ups.
              </div>
            </div>
            <button
              onClick={() => setLabMessages([])}
              style={{ border: "1px solid #E2E8F0", background: "#fff", borderRadius: 12, padding: "9px 13px", fontWeight: 850, cursor: "pointer", color: "#334155" }}
            >
              Limpar teste
            </button>
          </div>

          <div style={{ padding: 18 }}>
            <div style={{
              background: "linear-gradient(180deg,#0F172A,#111C33)",
              borderRadius: 24,
              padding: 18,
              minHeight: 620,
              maxHeight: 720,
              overflowY: "auto",
              boxShadow: "inset 0 0 0 1px rgba(255,255,255,.05)"
            }}>
              <div style={{ textAlign: "center", marginBottom: 18 }}>
                <span style={{ background: "rgba(255,255,255,.08)", color: "#CBD5E1", borderRadius: 999, padding: "6px 13px", fontSize: 11, fontWeight: 900 }}>
                  Simulação do atendimento
                </span>
              </div>

              {previewFlow.map((item, i) => {
                if (item.type === "stage") {
                  return (
                    <div key={i} style={{ textAlign: "center", margin: "18px 0 12px" }}>
                      <span style={{ background: "rgba(0,102,255,.18)", color: "#93C5FD", border: "1px solid rgba(147,197,253,.18)", borderRadius: 999, padding: "6px 12px", fontSize: 11, fontWeight: 900 }}>
                        {item.label}
                      </span>
                    </div>
                  );
                }

                if (item.type === "wait" || item.type === "followupTime") {
                  return (
                    <div key={i} style={{ textAlign: "center", margin: "14px 0" }}>
                      <span style={{ background: item.type === "wait" ? "rgba(245,158,11,.16)" : "rgba(124,58,237,.16)", color: item.type === "wait" ? "#FCD34D" : "#C4B5FD", borderRadius: 999, padding: "6px 12px", fontSize: 11, fontWeight: 900 }}>
                        {item.type === "wait" ? "⏳ " : "🔁 "}{item.text}
                      </span>
                    </div>
                  );
                }

                if (item.type === "media") {
                  const media = item.media || {};
                  return (
                    <div key={i} style={{ maxWidth: 340, background: "#fff", color: "#0F172A", borderRadius: "16px 16px 16px 4px", padding: 12, margin: "8px 0", boxShadow: "0 10px 25px rgba(0,0,0,.14)" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 42, height: 42, borderRadius: 14, background: "#EFF6FF", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>
                          {media.type === "audio" ? "🎤" : media.type === "video" ? "🎥" : media.type === "pdf" ? "📄" : "🖼️"}
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 12, fontWeight: 950, color: "#0F172A" }}>{mediaLabel(media)}</div>
                          <div style={{ fontSize: 11, color: "#64748B", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{media.name || media.mediaKey}</div>
                        </div>
                      </div>
                      {media.type === "audio" && (
                        <div style={{ marginTop: 10, background: "#F1F5F9", borderRadius: 999, padding: "8px 10px", display: "flex", alignItems: "center", gap: 8, fontSize: 11, fontWeight: 900, color: "#334155" }}>
                          ▶︎ <div style={{ height: 4, flex: 1, borderRadius: 999, background: "linear-gradient(90deg,#0066FF 45%,#CBD5E1 45%)" }} /> 00:18
                        </div>
                      )}
                    </div>
                  );
                }

                const isLead = item.type === "lead";
                return (
                  <div key={i} style={{ display: "flex", justifyContent: isLead ? "flex-end" : "flex-start", margin: "8px 0" }}>
                    <div style={{
                      maxWidth: "78%",
                      background: isLead ? "#DCFCE7" : "#FFFFFF",
                      color: isLead ? "#14532D" : "#0F172A",
                      borderRadius: isLead ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                      padding: "11px 13px",
                      fontSize: 13,
                      lineHeight: 1.55,
                      boxShadow: isLead ? "none" : "0 10px 25px rgba(0,0,0,.14)",
                      border: item.question ? "1px solid rgba(0,102,255,.18)" : "none"
                    }}>
                      {item.followup && <div style={{ color: "#7C3AED", fontSize: 10, fontWeight: 950, marginBottom: 4 }}>FOLLOW-UP AUTOMÁTICO</div>}
                      {item.text}
                      <div style={{ color: isLead ? "#16A34A" : "#64748B", fontSize: 10, textAlign: "right", marginTop: 5 }}>
                        {isLead ? "10:31 ✓✓" : "10:30"}
                      </div>
                    </div>
                  </div>
                );
              })}

              {labMessages.map((m, i) => (
                <div key={`lab-${i}`} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", margin: "8px 0" }}>
                  <div style={{
                    maxWidth: "78%",
                    background: m.role === "user" ? "#DCFCE7" : "#FFFFFF",
                    color: m.role === "user" ? "#14532D" : "#0F172A",
                    borderRadius: m.role === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                    padding: "11px 13px",
                    fontSize: 13,
                    lineHeight: 1.55
                  }}>
                    {m.content}
                  </div>
                </div>
              ))}

              {labLoading && (
                <div style={{ display: "flex", justifyContent: "flex-start", margin: "8px 0" }}>
                  <div style={{ background: "#fff", borderRadius: "16px 16px 16px 4px", padding: "11px 13px", fontSize: 13, color: "#64748B" }}>
                    Korax IA digitando...
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <aside style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 24, padding: 18, boxShadow: "0 18px 45px rgba(15,23,42,.06)" }}>
            <div style={{ fontWeight: 950, fontSize: 16, color: "#0F172A" }}>Resumo do agente</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 14 }}>
              {stats.map(s => (
                <div key={s.label} style={{ border: "1px solid #E2E8F0", borderRadius: 16, padding: 12, background: "#F8FAFC" }}>
                  <div style={{ fontSize: 19, fontWeight: 950, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: "#64748B", fontWeight: 850 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 24, padding: 18, boxShadow: "0 18px 45px rgba(15,23,42,.06)" }}>
            <div style={{ fontWeight: 950, fontSize: 16, color: "#0F172A" }}>Teste conversando</div>
            <div style={{ color: "#64748B", fontSize: 12, marginTop: 4, lineHeight: 1.5 }}>
              Digite como se fosse um lead e veja a resposta real da IA.
            </div>

            <textarea
              value={labInput}
              onChange={e => setLabInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleLabSend();
                }
              }}
              placeholder="Ex: Quanto custa? Tem demo? Funciona para clínica?"
              rows={5}
              style={{ width: "100%", marginTop: 14, padding: 12, borderRadius: 16, border: "1px solid #E2E8F0", outline: "none", fontFamily: "inherit", resize: "vertical", boxSizing: "border-box", fontSize: 13, lineHeight: 1.5 }}
            />

            <button
              onClick={handleLabSend}
              disabled={labLoading || !labInput.trim()}
              style={{
                width: "100%",
                marginTop: 10,
                border: "none",
                background: "linear-gradient(135deg,#0066FF,#00A3FF)",
                color: "#fff",
                borderRadius: 15,
                padding: "12px 14px",
                fontWeight: 950,
                cursor: labLoading || !labInput.trim() ? "not-allowed" : "pointer",
                opacity: labLoading || !labInput.trim() ? .55 : 1
              }}
            >
              {labLoading ? "Testando..." : "Enviar mensagem"}
            </button>
          </div>
        </aside>
      </div>
    );
  };

  const renderStep = () => {
    if (currentStep === 0) return renderAgente();
    if (currentStep === 1) return renderContexto();

    if (currentStep === 2) {
      if (directJourney) {
        return renderJornada();
      }

      if (!selectedExperience) {
        return renderSmartMode();
      }

      return selectedExperience === "journey"
        ? renderJornada()
        : renderSmartMode();
    }
    if (currentStep === 3) return renderObjecoes();
    return isSmartMode
      ? renderSmartPreview()
      : renderPreview();
  };

  if (onboardingMode) {
    return (
      <KoraxOnboardingInterview
        config={config}
        updateConfig={updateConfig}
        handleSave={handleSave}
        saving={saving}
        onCancel={() => history.push("/onboarding")}
      />
    );
  }

  return (
    <div className={classes.root}>
      {/* HEADER */}
      <div className={classes.header}>
        <IconButton size="small" onClick={() => history.push("/prompts")}
          style={{ borderRadius: 8, border: "1px solid #E2E8F0" }}>
          <ArrowBackIcon style={{ fontSize: 16 }} />
        </IconButton>
        <div className={classes.headerIcon}>🧠</div>
        <div style={{ flex: 1 }}>
          <div className={classes.headerTitle}>Treinamento da IA</div>
          <div className={classes.headerSub}>Ensine sua IA a atender, qualificar, vender e acompanhar leads.</div>
        </div>
        <button onClick={() => setCurrentStep(4)} style={{ border: "1px solid #E2E8F0", background: "#fff", color: "#0F172A", borderRadius: 12, padding: "9px 14px", fontWeight: 850, cursor: "pointer", fontSize: 12 }}>
          Testar agente
        </button>
        <button onClick={handleSave} disabled={saving} style={{ border: "none", background: "linear-gradient(135deg,#0066FF,#00A3FF)", color: "#fff", borderRadius: 12, padding: "9px 16px", fontWeight: 900, cursor: "pointer", fontSize: 12 }}>
          {saving ? "Salvando..." : "Salvar agente"}
        </button>
      </div>

      {/* WIZARD BAR */}
      <div className={classes.wizardBar}>
        {WIZARD_STEPS.map((ws, i) => (
          <React.Fragment key={ws.id}>
            <div
              className={`${classes.wizardStep} ${currentStep === i ? classes.wizardStepActive : ""} ${currentStep > i ? classes.wizardStepDone : ""}`}
              onClick={() => currentStep > i && setCurrentStep(i)}>
              <div className={`${classes.wizardStepNum} ${currentStep === i ? classes.wizardStepNumActive : ""} ${currentStep > i ? classes.wizardStepNumDone : ""}`}>
                {currentStep > i ? "✓" : i + 1}
              </div>
              {ws.icon} {ws.label}
            </div>
            {i < WIZARD_STEPS.length - 1 && <div className={classes.wizardDivider} />}
          </React.Fragment>
        ))}
      </div>

      {/* CONTENT */}
      <div className={classes.content}>

        {currentStep === 2 && selectedExperience && !directJourney && (
          <div style={{ marginBottom:16 }}>
            <button
              onClick={() => setSelectedExperience(null)}
              style={{
                border:"1px solid #E2E8F0",
                background:"#fff",
                padding:"8px 14px",
                borderRadius:10,
                cursor:"pointer",
                fontWeight:800
              }}
            >
              ← Trocar experiência
            </button>
          </div>
        )}
        {currentStep === 2 && !selectedExperience ? (
          <>
            {renderStep()}
            <div style={{ height: 20 }} />
          </>
        ) : (
        <div className={classes.premiumLayout}>
          <div className={classes.premiumMain}>
            {renderStep()}
            <div style={{ height: 20 }} />
          </div>

          <aside className={classes.premiumAside}>
            <div className={classes.checklistCard}>
              <div className={classes.checklistTitle}>Checklist do treinamento</div>
              {(isSmartMode ? smartChecklist : trainingChecklist).map((item, index) => (
                <div
                  key={index}
                  className={classes.checklistItem}
                  style={{
                    background: item.ok ? "rgba(34,197,94,0.06)" : "rgba(245,158,11,0.06)",
                    borderColor: item.ok ? "rgba(34,197,94,0.18)" : "rgba(245,158,11,0.20)"
                  }}
                >
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 900, color: item.ok ? "#16A34A" : "#B45309" }}>
                      {item.ok ? "✅" : "⚠️"} {item.label}
                    </div>
                    <div style={{ fontSize: 11, color: "#64748B", marginTop: 2, maxWidth: 190, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {item.value}
                    </div>
                  </div>
                </div>
              ))}

              <div className={classes.quickStatGrid}>
                <div className={classes.quickStat}>
                  <div style={{ fontSize: 18, fontWeight: 950, color: "#0066FF" }}>{steps.length}</div>
                  <div style={{ fontSize: 11, color: "#64748B", fontWeight: 700 }}>Etapas</div>
                </div>
                <div className={classes.quickStat}>
                  <div style={{ fontSize: 18, fontWeight: 950, color: "#7C3AED" }}>{objections.length}</div>
                  <div style={{ fontSize: 11, color: "#64748B", fontWeight: 700 }}>Objeções</div>
                </div>
                <div className={classes.quickStat}>
                  <div style={{ fontSize: 18, fontWeight: 950, color: "#16A34A" }}>{medias.length}</div>
                  <div style={{ fontSize: 11, color: "#64748B", fontWeight: 700 }}>Mídias</div>
                </div>
                <div className={classes.quickStat}>
                  <div style={{ fontSize: 18, fontWeight: 950, color: "#F59E0B" }}>{trainingScore}%</div>
                  <div style={{ fontSize: 11, color: "#64748B", fontWeight: 700 }}>Pronto</div>
                </div>
              </div>
            </div>
          </aside>
        </div>
        )}
      </div>

      {/* FOOTER NAV */}
      <div className={classes.footer}>
        <button className={classes.btnBack}
          onClick={() => currentStep > 0 ? setCurrentStep(s => s - 1) : history.push("/prompts")}
          style={{ opacity: 1 }}>
          <ArrowBackIcon style={{ fontSize: 16 }} />
          {currentStep === 0 ? "Cancelar" : "Voltar"}
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: "0.78rem", color: "#94A3B8" }}>
            Passo {currentStep + 1} de {WIZARD_STEPS.length}
          </span>
          {currentStep < WIZARD_STEPS.length - 1 ? (
            <button className={classes.btnNext}
              onClick={() => canAdvance() && setCurrentStep(s => s + 1)}
              style={{ opacity: canAdvance() ? 1 : 0.5, cursor: canAdvance() ? "pointer" : "not-allowed" }}>
              Próximo <ArrowForwardIcon style={{ fontSize: 16 }} />
            </button>
          ) : (
            <button className={classes.btnSave} onClick={handleSave} disabled={saving}>
              {saving ? <CircularProgress size={14} color="inherit" /> : <><SaveIcon style={{ fontSize: 16 }} /> Salvar agente</>}
            </button>
          )}
        </div>
      </div>

      {/* MEDIA MODAL */}
      {mediaModalOpen && (
        <div className={classes.mediaModal} onClick={e => e.target === e.currentTarget && setMediaModalOpen(false)}>
          <div className={classes.mediaModalBox}>
            <div className={classes.mediaModalHeader}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1rem" }}>📎</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "#fff" }}>Escolher mídia</div>
                <div style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.7)" }}>Selecione da Biblioteca Korax IA</div>
              </div>
              <button onClick={() => setMediaModalOpen(false)}
                style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", borderRadius: 7, width: 26, height: 26, cursor: "pointer", fontSize: "1rem" }}>×</button>
            </div>
            {medias.length === 0 ? (
              <div style={{ padding: 24, textAlign: "center" }}>
                <div style={{ fontSize: "2rem", marginBottom: 8 }}>📚</div>
                <div style={{ fontWeight: 600, fontSize: "0.85rem" }}>Nenhuma mídia cadastrada</div>
                <button onClick={() => { setMediaModalOpen(false); history.push("/korax-ia/biblioteca"); }}
                  style={{ marginTop: 12, background: "#0066FF", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontWeight: 700, fontSize: "0.8rem" }}>
                  Ir para Biblioteca →
                </button>
              </div>
            ) : (
              <div style={{ maxHeight: 320, overflowY: "auto" }}>
                {medias.map(m => {
                  const tc = TYPE_CONFIG[m.type] || TYPE_CONFIG.image;
                  return (
                    <div key={m.id} className={classes.mediaItem} onClick={() => addMediaToStep(mediaTargetStep, m)}>
                      <div style={{ width: 36, height: 36, borderRadius: 10, background: tc.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.1rem", flexShrink: 0 }}>{tc.emoji}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: "0.84rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.name}</div>
                        <div style={{ fontSize: "0.68rem", color: "#94A3B8", fontFamily: "monospace" }}>[ENVIAR_MIDIA: {m.mediaKey}]</div>
                      </div>
                      <Chip size="small" label={tc.label} style={{ background: tc.bg, color: tc.color, fontSize: "0.65rem", fontWeight: 700, height: 20 }} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default KoraxIABuilder;
